/**
 * Collection API Integration
 *
 * Updated to align with Ecommerce Search API Documentation
 * API Endpoints:
 * - GET /api/search/products - General product search
 * - GET /api/search/filter-options - Get available filter options
 * - GET /api/search/collections/{collection}/products - Search within collections
 *
 * Supports all documented filter parameters and response formats.
 * See: Ecommerce-Search-API-Documentation.md for complete API reference.
 */
class CollectionAPI {
  constructor() {
    this.baseUrl = window.COLLECTION_CONTEXT?.apiEndpoint || '/api/search/products';
    this.filterOptionsUrl = '/api/search/filter-options';
    this.currentPage = 1;
    this.currentFilters = {}; // User-selected filters for building API requests
    this.appliedFilters = {}; // Actual applied filters from API response for display
    this.currentSort = null;
    this.isLoading = false;
    this.initialPriceRange = null; // Store initial price range from API to prevent it from changing
    this.init();
  }

  init() {
    // Parse URL parameters on load
    this.parseUrlParams();

    // Update sort component with parsed value
    this.updateSortComponent();

    // Only load data if external API is enabled
    if (this.isExternalApiEnabled()) {
      // Load initial data
      this.loadData();
    }

    // Set up event listeners
    this.setupEventListeners();

    // Initialize mobile filter drawer after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.setupMobileFilterDrawer();
    }, 100);

    // dispatch event to collection area
    // document.dispatchEvent(new CustomEvent('collection:updated', {
    //   detail: {
    //     currentPage: this.currentPage,
    //   }
    // }));
  }

  updateSortComponent() {
    // Update sort component with current sort value
    setTimeout(() => {
      const sortComponent = document.querySelector('rt-sort-by');
      if (sortComponent && this.currentSort) {
        sortComponent.updateSort(this.currentSort);
      }
    }, 0);
  }

  parseUrlParams() {
    const params = new URLSearchParams(window.location.search);

    this.currentPage = parseInt(params.get('page')) || 1;

    // Handle both 'sort' and 'sort_by' parameters
    const sortParam = params.get('sort') || params.get('sort_by');
    this.currentSort = sortParam ? this.mapSortValue(sortParam) : null;

    // Parse filters from URL - handle array parameters correctly
    const processedKeys = new Set();
    params.forEach((value, key) => {
      if (key !== 'page' && key !== 'sort' && key !== 'sort_by') {
        // Handle array parameters (like collections[], colors[], etc.)
        const cleanKey = key.replace('[]', '');

        if (!processedKeys.has(cleanKey)) {
          processedKeys.add(cleanKey);
          // Get all values for this parameter
          const allValues = params.getAll(key);

          if (allValues.length > 1) {
            // Multiple values from array parameters
            this.currentFilters[cleanKey] = allValues;
          } else if (allValues.length === 1) {
            // Single value - could be comma-separated or single
            if (cleanKey === 'price') {
              // Special handling for price parameter - convert combined range to individual ranges
              this.currentFilters[cleanKey] = this.expandPriceRangeToIndividualRanges(value);
            } else {
              this.currentFilters[cleanKey] = value.includes(',') ? value.split(',') : [value];
            }
          }
        }
      }
    });

    // Parse collection from URL path and add to filters if not already in URL parameters
    const pathParts = window.location.pathname.split('/');
    const pathCollection =
      pathParts[1] === 'collections' && pathParts[2] && pathParts[2] !== 'all'
        ? pathParts[2]
        : null;

    if (pathCollection) {
      // Add collection from path if not already present in URL params
      if (!this.currentFilters.collections) {
        this.currentFilters.collections = [pathCollection];
      } else if (!this.currentFilters.collections.includes(pathCollection)) {
        // If collections filter exists but doesn't include the path collection, add it
        this.currentFilters.collections.push(pathCollection);
      }
    }
  }

  /**
   * Convert a combined price range (e.g., "500-9999") into individual price ranges
   * that fall within that range for proper checkbox selection
   */
  expandPriceRangeToIndividualRanges(combinedRange) {
    // Parse the combined range
    const rangeParts = combinedRange.split('-');
    const minPrice = parseFloat(rangeParts[0]) || 0;
    const maxPrice = parseFloat(rangeParts[1]) || Number.MAX_VALUE;

    // Define all possible price ranges (same as in generateDynamicPriceRanges)
    const allPossibleRanges = [
      { min: 0, max: 500, value: '0-500' },
      { min: 500, max: 1500, value: '500-1500' },
      { min: 1500, max: 5000, value: '1500-5000' },
      { min: 5000, max: 9999, value: '5000-9999' },
      { min: 10000, max: 19999, value: '10000-19999' },
      { min: 20000, max: 999999900, value: '20000-999999900' },
    ];

    // Find all ranges that are completely contained within the combined range
    const containedRanges = allPossibleRanges.filter((range) => {
      // A range is selected only if it's completely within the combined filter range
      return range.min >= minPrice && range.max <= maxPrice;
    });

    return containedRanges.map((range) => range.value);
  }

  /**
   * Build API URL according to Ecommerce Search API documentation
   * API Endpoint: GET /api/search/products
   *
   * Supported filters:
   * - q: Search query string (optional)
   * - tags[]: Filter by product tags (array)
   * - collections[]: Filter by collections (array) - supports multiple collections selection
   *                  When multiple collections are selected, products from any of the selected
   *                  collections will be returned. URL will use /collections/all with collections[]
   *                  query parameters for multiple selections.
   * - colors[]: Filter by colors (array)
   * - sizes[]: Filter by sizes (array)
   * - gemstones[]: Filter by gemstones (array)
   * - enamels[]: Filter by enamels (array)
   * - options[]: Filter by options (array)
   * - vendor[]: Filter by vendors (array)
   * - product_type: Filter by product type (string)
   * - price_min: Minimum price filter (numeric)
   * - price_max: Maximum price filter (numeric)
   * - status: Product status (active, draft, archived)
   * - in_stock: Filter by stock availability (boolean)
   * - sort_by: Sort order (relevance, price_asc, price_desc, title_asc, title_desc, created_desc, collection_order)
   * - per_page: Items per page (1-250 for general search, 1-100 for collection search)
   * - page: Page number (minimum 1)
   *
   * Note: Collections filtering supports both single and multiple selection.
   * - Single collection: Uses traditional path-based routing (/collections/{handle})
   * - Multiple collections: Uses /collections/all with collections[] query parameters
   * Collections can be selected via the filters interface and will override the default
   * collection context when selected.
   */
  buildApiUrl(baseUrl = this.baseUrl, filters = {}, page = 1, perPage = 24, sortBy = null) {
    const url = new URL(baseUrl, window.location.origin);

    // Work with a shallow copy to avoid mutating the caller's filters object (e.g. this.currentFilters)
    const safeFilters = { ...filters };
    if (Array.isArray(filters.collections)) {
      safeFilters.collections = [...filters.collections];
    }

    // Add pagination parameters
    url.searchParams.set('page', page);
    url.searchParams.set('per_page', perPage);

    // Add sorting parameter
    if (sortBy) {
      url.searchParams.set('sort_by', sortBy);
    } else {
      url.searchParams.set('sort_by', 'collection_order'); // Default sort
    }

    // Handle collection filtering logic
    // Always use safeFilters.collections if available, no fallback to COLLECTION_CONTEXT
    // since parseUrlParams ensures path collections are included in filters
    if (safeFilters.collections && safeFilters.collections.length > 0) {
      // Only a /collections/<handle> path names a real collection. Taking the
      // last path segment unconditionally turned /search into a "search"
      // collection filter, and since the API ANDs collections together that
      // wiped out every result as soon as a category was selected on the
      // search page.
      const pathParts = window.location.pathname.split('/');
      const collectionHandle =
        pathParts[1] === 'collections' && pathParts[2] && pathParts[2] !== 'all'
          ? pathParts[2]
          : null;

      if (collectionHandle) {
        // The collection being browsed always leads, so the API sorts by its order
        safeFilters.collections = safeFilters.collections.filter((c) => c !== collectionHandle);
        safeFilters.collections.unshift(collectionHandle);
      }

      safeFilters.collections.forEach((collection) => {
        url.searchParams.append('collections[]', collection);
      });
    }

    // Add filters according to API documentation
    Object.entries(safeFilters).forEach(([key, values]) => {
      if (key === 'collections') {
        // Skip collections filtering as it's handled separately
        return;
      }
      if (key === 'q' && values) {
        // Handle search query parameter - extract plain string from array if needed
        const qValue = Array.isArray(values) ? values[0] : values;
        if (qValue) {
          url.searchParams.set('q', qValue);
        }
        return;
      }
      if (key === 'price' && Array.isArray(values) && values.length > 0) {
        // Handle multiple price range filtering by finding min and max from all selected ranges
        let minPrice = null;
        let maxPrice = null;

        values.forEach((priceRange) => {
          const rangeParts = priceRange.split('-');
          const rangeMin = rangeParts[0] && rangeParts[0] !== '' ? parseFloat(rangeParts[0]) : null;
          const rangeMax = rangeParts[1] && rangeParts[1] !== '' ? parseFloat(rangeParts[1]) : null;

          // Find the overall minimum price from all ranges
          if (rangeMin !== null && (minPrice === null || rangeMin < minPrice)) {
            minPrice = rangeMin;
          }

          // Find the overall maximum price from all ranges
          if (rangeMax !== null && (maxPrice === null || rangeMax > maxPrice)) {
            maxPrice = rangeMax;
          }
        });

        // Log the combined price range for debugging
        if (values.length > 1) {
          console.log(
            'CollectionAPI: Multiple price ranges selected:',
            values,
            '-> Combined range:',
            { min: minPrice, max: maxPrice }
          );
        }

        // Set the combined price range parameters
        if (minPrice !== null) {
          url.searchParams.set('price_min', minPrice.toString());
        }
        if (maxPrice !== null) {
          url.searchParams.set('price_max', maxPrice.toString());
        }
      } else if (key === 'product_type' && values) {
        // Handle single product type filter
        url.searchParams.set('product_type', values);
      } else if (key === 'status' && values) {
        // Handle status filter (active, draft, archived)
        url.searchParams.set('status', values);
      } else if (key === 'in_stock' && values !== undefined) {
        // Handle stock availability filter (boolean)
        url.searchParams.set('in_stock', values);
      } else if (Array.isArray(values) && values.length > 0) {
        // Handle array filters with [] notation for:
        // tags[], collections[], colors[], sizes[], gemstones[], enamels[], options[], vendor[]
        values.forEach((value) => {
          url.searchParams.append(`${key}[]`, value);
        });
      } else if (values) {
        // Handle any other single value filters
        url.searchParams.set(key, values);
      }
    });

    if (window?.showOnlyInStock) {
      url.searchParams.set('expose_in_stock', 1);
    }

    console.log('BUILD API URL:', url);

    const finalUrl = url.toString();
    return finalUrl;
  }

  async loadData() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();

    // On search pages, ensure the ?q parameter from the URL is always forwarded
    // to the API even if it somehow fell out of currentFilters.
    const filtersForApi = { ...this.currentFilters };
    if (window.location.pathname.startsWith('/search') && !filtersForApi.q) {
      const urlQ = new URLSearchParams(window.location.search).get('q');
      if (urlQ) {
        filtersForApi.q = urlQ;
      }
    }

    const apiUrl = this.buildApiUrl(
      this.baseUrl,
      filtersForApi,
      this.currentPage,
      48,
      this.currentSort
    );

    console.log('CollectionAPI: Fetching data from API URL:', apiUrl);

    try {
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const apiData = await response.json();

      // Store the last API response for accessing facets and other data
      this.lastApiResponse = apiData;

      // Store applied filters from API response for active filters display
      // This ensures we show what filters are actually applied by the backend
      this.appliedFilters = apiData.applied_filters || {};

      // Store initial price range from API facets on first load (to prevent it from changing with filters)
      if (!this.initialPriceRange && apiData.facets && apiData.facets.price) {
        const priceFacet = apiData.facets.price;
        if (priceFacet.options) {
          if (Array.isArray(priceFacet.options)) {
            this.initialPriceRange = {
              min: priceFacet.options[0] || 0,
              max: (priceFacet.options[1] || 1000) + 1000,
            };
          } else if (typeof priceFacet.options === 'object') {
            this.initialPriceRange = {
              min: priceFacet.options.min || 0,
              max: (priceFacet.options.max || 1000) + 1000,
            };
          }
        } else if (priceFacet.min !== undefined || priceFacet.max !== undefined) {
          this.initialPriceRange = {
            min: priceFacet.min || 0,
            max: (priceFacet.max || 1000) + 1000,
          };
        }
      }

      // Transform API data to match our custom elements
      const transformedData = this.transformApiData(apiData);

      // Store current products for price range calculations
      this.currentProducts = transformedData.products;

      this.renderProducts(transformedData.products);
      this.renderFilters(transformedData.filters);
      this.renderPagination(transformedData.pagination);
      this.updateProductCount(transformedData.pagination.total_products);
      this.updateActiveFilters();
      this.updateSidebarCheckedState();
      this.updateActiveFiltersCount();

      // Price filters now use predefined ranges and don't need dynamic updates
    } catch (error) {
      this.showError('Failed to load products');
    } finally {
      this.isLoading = false;
      this.hideLoading();

      setTimeout(() => {
        // Dispatch collection:updated event after rendering is complete
        document.dispatchEvent(new CustomEvent('collection:updated'));
      }, 500);
    }
  }

  updateActiveFilters() {
    const activeFiltersElement = document.querySelector('rt-active-filters');
    if (activeFiltersElement) {
      // Use applied filters from API response instead of current filters
      // Fallback to current filters if applied filters not yet available
      const filtersToUse =
        Object.keys(this.appliedFilters).length > 0 ? this.appliedFilters : this.currentFilters;

      // Exclude pagination parameters from active filters display
      const filtersForDisplay = { ...filtersToUse };
      delete filtersForDisplay.page;
      delete filtersForDisplay.per_page;

      activeFiltersElement.updateActiveFilters(filtersForDisplay);
    }

    // Also update the mobile filter button count
    this.updateActiveFiltersCount();
  }

  /**
   * Transform API response data according to Ecommerce Search API documentation
   *
   * Expected API Response Format:
   * {
   *   "data": [...], // Array of products
   *   "meta": {
   *     "total": 150,
   *     "per_page": 20,
   *     "current_page": 1,
   *     "last_page": 8,
   *     "from": 1,
   *     "to": 20
   *   },
   *   "facets": {...} // Available filter facets
   * }
   */
  transformApiData(apiData) {
    // Validate API response structure
    if (!apiData || !apiData.data || !apiData.meta) {
      return {
        products: [],
        filters: [],
        pagination: {
          current_page: 1,
          total_pages: 1,
          total_products: 0,
          per_page: 48,
          has_previous: false,
          has_next: false,
        },
      };
    }

    // Check for facets in API response
    if (!apiData.facets) {
    }

    // Transform products from API format (already in correct format)
    const products = apiData.data.map((product) => product);

    // Transform facets to filters according to API structure
    // Provide empty object as fallback if facets are missing

    const filters = this.transformFacetsToFilters(apiData.facets || {});

    // Transform pagination according to API meta structure
    const pagination = {
      current_page: apiData.meta.current_page || 1,
      total_pages: apiData.meta.last_page || 1,
      total_products: apiData.meta.total || 0,
      per_page: apiData.meta.per_page || 48,
      has_previous: (apiData.meta.current_page || 1) > 1,
      has_next: (apiData.meta.current_page || 1) < (apiData.meta.last_page || 1),
      from: apiData.meta.from || 0,
      to: apiData.meta.to || 0,
    };

    return { products, filters, pagination };
  }

  extractProductImages(product) {
    // Extract images from product data
    let images = [];

    // Handle different image formats from API
    if (product.images && Array.isArray(product.images)) {
      images = product.images;
    } else if (product.image) {
      images = [product.image];
    } else if (product.featured_image) {
      images = [product.featured_image];
    } else {
      // Fallback to constructing image URLs based on handle
      const baseImageUrl = `https://cdn.shopify.com/s/files/1/0234/5678/products/`;
      return [
        {
          src: `${baseImageUrl}${product.handle}-1.jpg`,
          alt: product.title,
        },
        {
          src: `${baseImageUrl}${product.handle}-2.jpg`,
          alt: `${product.title} - Alternate View`,
        },
      ];
    }

    // Ensure images have proper Shopify CDN URLs
    return images.map((image) => {
      if (typeof image === 'string') {
        return { src: this.ensureShopifyImageUrl(image), alt: product.title };
      }
      return {
        src: this.ensureShopifyImageUrl(image.src || image.url),
        alt: image.alt || product.title,
      };
    });
  }

  ensureShopifyImageUrl(imageUrl) {
    if (!imageUrl) return '';

    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }

    // If it's a Shopify CDN path, ensure it has the correct domain
    if (imageUrl.startsWith('//cdn.shopify.com') || imageUrl.startsWith('/cdn.shopify.com')) {
      return `https:${imageUrl}`;
    }

    // If it's a relative path, assume it's a Shopify asset
    if (imageUrl.startsWith('/')) {
      return `${window.location.origin}${imageUrl}`;
    }

    return imageUrl;
  }

  /**
   * Transform API facets to filter format for UI components
   *
   * API facets structure should contain available filter options based on current search results.
   * Supports all documented filter types: tags, colors, sizes, gemstones, enamels, options, vendor, collections, etc.
   *
   * Note: Collections filter is now included in the UI for user selection.
   */
  transformFacetsToFilters(facets) {
    const filters = [];

    if (!facets || typeof facets !== 'object') {
      return this.getDefaultFilters();
    }

    Object.entries(facets).forEach(([key, facet]) => {
      // Handle different facet structures that might come from the API
      const filter = {
        id: facet.key || key,
        field_name: facet.field_name || key,
        label: facet.label || this.getFilterLabel(key),
        type: facet.type || 'list',
        is_swatch: facet.is_swatch,
        values: this.transformFacetValues(facet.options || facet.values || facet),
      };

      // Special handling for price filter - use dynamic ranges based on facet data
      if (filter.id === 'price') {
        filter.type = 'list';
        filter.values = this.generateDynamicPriceRanges(facet);
      }

      // Special handling for collections filter
      if (filter.id === 'collections') {
        filter.type = 'list'; // Ensure collections are rendered as a list
      }

      // Only add filters that have values or are special types
      if (
        (Array.isArray(filter.values) && filter.values.length > 0) ||
        (filter.id === 'price' && filter.values) ||
        (filter.id === 'collections' && filter.values && filter.values.length > 0)
      ) {
        filters.push(filter);
      }
    });

    return filters;
  }

  /**
   * Transform facet values to the expected format for UI components
   */
  transformFacetValues(values) {
    if (!values) {
      return [];
    }

    // Handle case where values is an object with array properties
    if (!Array.isArray(values) && typeof values === 'object') {
      // Try common property names for arrays
      if (Array.isArray(values.options)) {
        values = values.options;
      } else if (Array.isArray(values.values)) {
        values = values.values;
      } else if (Array.isArray(values.items)) {
        values = values.items;
      } else {
        // Convert object to array of values
        values = Object.values(values);
      }
    }

    if (!Array.isArray(values)) {
      return [];
    }

    const transformedValues = values.map((value) => {
      if (typeof value === 'string') {
        return { value: value, label: value, count: 0 };
      }
      return {
        value: value.value || value.key || value.handle || value,
        label:
          value.label ||
          value.name ||
          value.title ||
          value.value ||
          value.key ||
          value.handle ||
          value,
        count: value.count || value.doc_count || value.product_count || 0,
      };
    });

    return transformedValues;
  }

  /**
   * Generate dynamic price ranges based on actual min/max prices from facet data
   * Only shows price ranges that are applicable based on the available product prices
   * @param {Object} priceFacet - The price facet object from API response
   * @returns {Array} Array of applicable price range objects
   */
  generateDynamicPriceRanges(priceFacet) {
    // Extract min and max prices from facet data
    let minPrice = 0;
    let maxPrice = 999999900; // Default very high value

    // Handle facet data structure based on your API format
    if (priceFacet && priceFacet.options && Array.isArray(priceFacet.options)) {
      // The first element is the minimum price, last element is the maximum price
      minPrice = parseFloat(priceFacet.options[0]) || 0;
      maxPrice = parseFloat(priceFacet.options[priceFacet.options.length - 1]) || 999999900;
      console.log('CollectionAPI: Price facet data:', {
        minPrice,
        maxPrice,
        options: priceFacet.options,
      });
    }

    // If we couldn't get prices from facet, try other fallbacks
    if (minPrice === 0 && maxPrice === 999999900) {
      // Fallback to current products if available
      if (this.currentProducts && this.currentProducts.length > 0) {
        const productPrices = this.currentProducts
          .map((product) => parseFloat(product.price) || 0)
          .filter((price) => price > 0);

        if (productPrices.length > 0) {
          minPrice = Math.min(...productPrices);
          maxPrice = Math.max(...productPrices);
          console.log('CollectionAPI: Using product prices fallback:', { minPrice, maxPrice });
        }
      }
    }

    // Define all possible price ranges
    const allPriceRanges = [
      { min: 0, max: 500, label: 'Under $500' },
      { min: 500, max: 1500, label: '$500 - $1,500' },
      { min: 1500, max: 5000, label: '$1,500 - $5,000' },
      { min: 5000, max: 9999, label: '$5,000 - $9,999' },
      { min: 10000, max: 19999, label: '$10,000 - $19,999' },
      { min: 20000, max: 999999900, label: '$20,000+' },
    ];

    // Filter ranges to only show applicable ones
    const applicableRanges = allPriceRanges.filter((range) => {
      // Show range if:
      // 1. The range minimum is less than or equal to the max available price, AND
      // 2. The range maximum is greater than or equal to the min available price
      const isApplicable = range.min <= maxPrice && range.max >= minPrice;
      return isApplicable;
    });

    // Convert to the expected format
    return applicableRanges.map((range) => ({
      value: `${range.min}-${range.max}`,
      label: range.label,
      count: 0, // Could be enhanced to show actual counts if API provides them
    }));
  }

  /**
   * Get human-readable labels for filter keys
   */
  getFilterLabel(key) {
    const labelMap = {
      tags: 'Tags',
      collections: 'Collections',
      colors: 'Colors',
      sizes: 'Sizes',
      gemstones: 'Gemstones',
      enamels: 'Enamels',
      options: 'Options',
      vendor: 'Vendor',
      product_type: 'Product Type',
      status: 'Status',
    };
    return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
  }

  /**
   * Get default filters when facets are not available
   */
  getDefaultFilters() {
    return [
      {
        id: 'price',
        label: 'Price Range',
        type: 'list',
        values: this.generateDynamicPriceRanges(null), // Use dynamic ranges even for defaults
      },
    ];
  }

  /**
   * Fetch available filter options from the API
   * Endpoint: GET /api/search/filter-options
   *
   * This endpoint returns all available values for filters like tags, colors, sizes,
   * gemstones, enamels, vendors, product types, etc.
   * Useful for building dynamic filter interfaces.
   */
  async loadFilterOptions() {
    try {
      const response = await fetch(this.filterOptionsUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const filterOptionsData = await response.json();

      return filterOptionsData.data || {};
    } catch (error) {
      console.error('Error loading filter options:', error);
      return {};
    }
  }

  /**
   * Set search query and reload data
   */
  setSearchQuery(query) {
    if (query && query.trim()) {
      this.currentFilters.q = query.trim();
    } else {
      delete this.currentFilters.q;
    }
    this.currentPage = 1; // Reset to first page
    this.loadData();
  }

  /**
   * Add support for stock availability filtering
   */
  setStockFilter(inStock) {
    if (inStock !== undefined) {
      this.currentFilters.in_stock = inStock;
    } else {
      delete this.currentFilters.in_stock;
    }
    this.currentPage = 1; // Reset to first page
    this.loadData();
  }

  /**
   * Add support for product status filtering
   */
  setStatusFilter(status) {
    const validStatuses = ['active', 'draft', 'archived'];
    if (status && validStatuses.includes(status)) {
      this.currentFilters.status = status;
    } else {
      delete this.currentFilters.status;
    }
    this.currentPage = 1; // Reset to first page
    this.loadData();
  }

  renderProducts(products) {
    const productList = document.querySelector('rt-product-list');
    if (productList) {
      productList.setProducts(products);
    }
  }

  renderFilters(filters) {
    // Update all rt-filters elements (both in sidebar and in drawer)
    const filtersElements = document.querySelectorAll('rt-filters');

    filtersElements.forEach((filtersElement, index) => {
      filtersElement.setFilters(filters);
    });

    // Also update any filters drawers specifically
    const filtersDrawers = document.querySelectorAll('rt-filters-drawer');

    filtersDrawers.forEach((drawer, index) => {
      drawer.setFilters(filters);
    });
  }

  renderPagination(pagination) {
    const paginateElement = document.querySelector('rt-paginate');
    if (paginateElement) {
      paginateElement.setPagination(pagination);
    }
  }

  updateProductCount(count) {
    const countElement = document.getElementById('products-count');
    if (countElement) {
      countElement.textContent = count;
    }

    // Update search results count element if it exists
    const searchResultsCountElement = document.querySelector('.search-results-count');
    if (searchResultsCountElement) {
      searchResultsCountElement.innerHTML = count;
    }
  }

  showLoading() {
    document.getElementById('loading-overlay')?.classList.add('active');
  }

  hideLoading() {
    document.getElementById('loading-overlay')?.classList.remove('active');
  }

  showError(message) {
    // You can implement a more sophisticated error display
    console.error(message);
  }

  updateUrl() {
    const params = new URLSearchParams();
    if (this.currentPage > 1) {
      params.set('page', this.currentPage);
    }

    // Add sort parameter if set - use sort_by for API compatibility
    if (this.currentSort) {
      params.set('sort_by', this.currentSort);
    } else {
      params.set('sort_by', 'collection_order'); // Default sort
    }

    // Always stay on current path - don't navigate between collection pages
    const newPath = window.location.pathname;

    // Get current collection from path if it exists and is not "all"
    const pathParts = window.location.pathname.split('/');
    const currentPathCollection =
      pathParts[1] === 'collections' && pathParts[2] && pathParts[2] !== 'all'
        ? pathParts[2]
        : null;

    // Add all filters as query parameters, including collections
    Object.entries(this.currentFilters).forEach(([key, values]) => {
      if (key === 'collections') {
        // Always add collections as query parameters
        if (Array.isArray(values) && values.length > 0) {
          values.forEach((collection) => {
            params.append('collections[]', collection);
          });
        }
      } else if (key === 'price' && Array.isArray(values) && values.length > 0) {
        // Handle price filters by combining multiple ranges into a single min-max range
        let minPrice = null;
        let maxPrice = null;

        values.forEach((priceRange) => {
          const rangeParts = priceRange.split('-');
          const rangeMin = rangeParts[0] && rangeParts[0] !== '' ? parseFloat(rangeParts[0]) : null;
          const rangeMax = rangeParts[1] && rangeParts[1] !== '' ? parseFloat(rangeParts[1]) : null;

          // Find the overall minimum price from all ranges
          if (rangeMin !== null && (minPrice === null || rangeMin < minPrice)) {
            minPrice = rangeMin;
          }

          // Find the overall maximum price from all ranges
          if (rangeMax !== null && (maxPrice === null || rangeMax > maxPrice)) {
            maxPrice = rangeMax;
          }
        });

        // Set combined price range in URL
        if (minPrice !== null && maxPrice !== null) {
          params.set('price', `${minPrice}-${maxPrice}`);
        } else if (minPrice !== null) {
          params.set('price', `${minPrice}-`);
        } else if (maxPrice !== null) {
          params.set('price', `-${maxPrice}`);
        }

        // Log the URL price parameter for debugging
        if (values.length > 1) {
          console.log(
            'CollectionAPI: Multiple price ranges in URL:',
            values,
            '-> Combined URL parameter:',
            params.get('price')
          );
        }
      } else {
        // Handle all other filters
        if (Array.isArray(values) && values.length > 0) {
          params.set(key, values.join(','));
        } else if (values) {
          params.set(key, values);
        }
      }
    });

    // If no collection filters are selected but we're on a specific collection path,
    // add the current path collection as a parameter
    if (
      (!this.currentFilters.collections || this.currentFilters.collections.length === 0) &&
      currentPathCollection
    ) {
      params.append('collections[]', currentPathCollection);
    }

    // Safety net: always preserve the ?q parameter on search pages.
    // If q somehow fell out of currentFilters (e.g. after clearAllFilters), re-read it
    // from the current URL so pagination never loses the search query.
    const isSearchPage = window.location.pathname.startsWith('/search');
    if (isSearchPage && !params.has('q')) {
      const currentUrlParams = new URLSearchParams(window.location.search);
      const currentQ = currentUrlParams.get('q');
      if (currentQ) {
        params.set('q', currentQ);
      }
    }

    const newUrl = `${newPath}${params.toString() ? '?' + params.toString() : ''}`;

    if (newUrl !== window.location.pathname + window.location.search) {
      // Use pushState for filter changes (no page navigation)
      window.history.pushState({}, '', newUrl);

      // Update the collection context
      if (this.currentFilters.collections && this.currentFilters.collections.length > 0) {
        if (this.currentFilters.collections.length === 1) {
          // Single collection - update context to that collection
          this.updateCollectionContext(this.currentFilters.collections[0]);
        } else {
          // Multiple collections - set context to 'all'
          this.updateCollectionContext('all');
        }
      } else if (currentPathCollection) {
        // No filter selections but on a collection path - use that collection
        this.updateCollectionContext(currentPathCollection);
      } else {
        // No collection context
        this.updateCollectionContext('all');
      }

      console.log('CollectionAPI: URL updated to:', newUrl);
    }
  }

  updateCollectionContext(handle) {
    // Update the global collection context
    if (window.COLLECTION_CONTEXT) {
      window.COLLECTION_CONTEXT.handle = handle;
    }
  }

  updateSidebarCheckedState() {
    // Update sidebar filter inputs to match current filters
    Object.entries(this.currentFilters).forEach(([filterKey, filterValues]) => {
      if (Array.isArray(filterValues)) {
        filterValues.forEach((value) => {
          // Find and check the corresponding checkbox for all filters including collections
          const checkbox = document.querySelector(
            `rt-filter[data-filter*='"id":"${filterKey}"'] input[type="checkbox"][value="${value}"]`
          );
          if (checkbox) {
            checkbox.checked = true;
          }
        });
      }
    });

    // Uncheck any inputs that are not in current filters
    document
      .querySelectorAll('rt-filter input[type="checkbox"], rt-filter input[type="radio"]')
      .forEach((input) => {
        const filterElement = input.closest('rt-filter');
        if (filterElement) {
          const filterData = JSON.parse(filterElement.getAttribute('data-filter'));
          const filterId = filterData.id;
          const inputValue = input.value;

          if (
            !this.currentFilters[filterId] ||
            !this.currentFilters[filterId].includes(inputValue)
          ) {
            input.checked = false;
          }
        }
      });
  }

  /**
   * Map Shopify sort values to API sort values
   */
  mapSortValue(sortValue) {
    const sortMap = {
      relevance: 'relevance',
      price_asc: 'price_asc',
      price_desc: 'price_desc',
      title_asc: 'title_asc',
      title_desc: 'title_desc',
      created_desc: 'created_desc',
      collection_order: 'collection_order',
      // Map Shopify default sort values
      manual: 'collection_order',
      'best-selling': 'relevance',
      'title-ascending': 'title_asc',
      'title-descending': 'title_desc',
      'price-ascending': 'price_asc',
      'price-descending': 'price_desc',
      'created-ascending': 'created_desc',
      'created-descending': 'created_desc',
    };

    return sortMap[sortValue] || 'relevance';
  }

  updateSort(sortValue) {
    // Map the sort value to API format
    const apiSortValue = this.mapSortValue(sortValue);

    this.currentSort = apiSortValue;
    this.currentPage = 1; // Reset to first page when sorting changes

    // Update URL and load data
    this.updateUrl();
    this.loadData();

    // Update sort components
    const sortComponents = document.querySelectorAll('rt-sort-by');
    sortComponents.forEach((component) => {
      component.updateSort(sortValue); // Keep original value for UI
    });

    // Also update any existing facets-sort-popover components
    const popovers = document.querySelectorAll('facets-sort-popover');
    popovers.forEach((popover) => {
      const listbox = popover.querySelector('x-listbox');
      if (listbox) {
        // Update the selected option in the listbox
        const options = listbox.querySelectorAll('.popover-listbox__option');
        options.forEach((option) => {
          if (option.value === sortValue) {
            option.setAttribute('aria-selected', 'true');
          } else {
            option.removeAttribute('aria-selected');
          }
        });
      }
    });
  }

  /**
   * Remove a specific filter value from the current filters
   *
   * @param {string} key - The filter key (e.g., 'colors', 'tags', 'collections')
   * @param {string} value - The filter value to remove
   *
   * Special handling for collections filters:
   * - Collections filters affect the URL path, not just query parameters
   * - Removing a collections filter may trigger navigation to /collections/all
   * - Collections filters should normally be hidden from the UI, but this provides
   *   proper handling if they somehow appear in active filters
   */
  removeFilter(key, value) {
    if (this.currentFilters[key]) {
      if (Array.isArray(this.currentFilters[key])) {
        this.currentFilters[key] = this.currentFilters[key].filter((v) => v !== value);
        if (this.currentFilters[key].length === 0) {
          delete this.currentFilters[key];
        }
      } else {
        delete this.currentFilters[key];
      }

      // Special handling for collections filter removal
      if (key === 'collections') {
        // When removing a collections filter, the URL path needs to be updated
        // This may trigger navigation to /collections/all or another collection page
        this.currentPage = 1;
        this.updateUrl(); // This will handle the URL path change and possible navigation
        this.loadData();
      } else {
        // For all other filters, update URL query parameters and reload data
        this.currentPage = 1; // Reset to first page when filters change
        this.updateUrl(); // Update URL to reflect filter removal
        this.loadData();
      }

      // Update active filters display and mobile filter button count
      this.updateActiveFilters();
      this.updateSidebarCheckedState();
    }
  }

  clearAllFilters() {
    // Preserve collections filter when clearing all other filters
    // Collections are determined by the current collection page context
    // Only add as a collection filter when we're actually on a /collections/ path
    const pathParts = window.location.pathname.split('/');
    const collectionHandle =
      pathParts[1] === 'collections' && pathParts[2] && pathParts[2] !== 'all'
        ? pathParts[2]
        : null;

    // Preserve the search query (?q=) when clearing filters on the search page
    const searchQuery = this.currentFilters.q;

    this.currentFilters = {};
    if (collectionHandle) {
      this.currentFilters.collections = [collectionHandle];
    }
    // Restore the search query on search pages so pagination still works
    if (searchQuery && window.location.pathname.startsWith('/search')) {
      this.currentFilters.q = searchQuery;
    }
    this.currentPage = 1;
    this.updateUrl();
    this.loadData();
    this.updateActiveFilters();
    this.updateSidebarCheckedState();
  }

  applyFilters() {
    // This method can be used to trigger a fetch when filters are applied
    this.currentPage = 1;
    this.loadData();

    // Update active filters and mobile filter button count
    this.updateActiveFilters();
    this.updateSidebarCheckedState();

    // Close mobile filter drawer if it's open
    const filtersDrawer = document.querySelector('rt-filters-drawer');
    if (filtersDrawer && filtersDrawer.classList.contains('is-open')) {
      filtersDrawer.close();
    }
  }

  updateFilters(filters) {
    this.currentFilters = { ...filters };
    this.currentPage = 1; // Reset to first page when filters change
    this.loadData();
  }

  updatePage(page) {
    this.currentPage = page;
    this.loadData();
  }

  updateActiveFilters() {
    const activeFiltersElement = document.querySelector('rt-active-filters');
    if (activeFiltersElement) {
      // Use applied filters from API response instead of current filters
      // Fallback to current filters if applied filters not yet available
      const filtersToUse =
        Object.keys(this.appliedFilters).length > 0 ? this.appliedFilters : this.currentFilters;

      // Exclude pagination parameters from active filters display
      const filtersForDisplay = { ...filtersToUse };
      delete filtersForDisplay.page;
      delete filtersForDisplay.per_page;

      activeFiltersElement.updateActiveFilters(filtersForDisplay);
    }
  }

  /**
   * Check if external API is enabled for this page
   */
  isExternalApiEnabled() {
    // Check if we're on a collection page with external API enabled
    const collectionSection = document.querySelector(
      '[data-section-type="main-collection"], [data-section-type="main-collection-with-promo"]'
    );
    if (collectionSection) {
      const useExternalApi = collectionSection.querySelector('[data-setting="use_external_api"]');
      if (useExternalApi) {
        // Try both textContent and value attributes
        const enabled = useExternalApi.textContent === 'true' || useExternalApi.value === 'true';
        return enabled;
      }
    }

    // Check for search page with external API
    const searchSection = document.querySelector('[data-section-type="main-search"]');
    if (searchSection) {
      const useExternalApi = searchSection.querySelector('[data-setting="use_external_api"]');
      if (useExternalApi) {
        // Try both textContent and value attributes
        const enabled = useExternalApi.textContent === 'true' || useExternalApi.value === 'true';
        return enabled;
      }
    }

    // Fallback: Check if we have rt-sort-by components or other indicators
    const hasRTSortBy = document.querySelector('rt-sort-by');
    const hasExternalAPIIndicators =
      document.querySelector('.collection-api__filters') ||
      document.querySelector('[data-setting="use_external_api"]');

    // If we have rt-sort-by components, assume external API is enabled
    if (hasRTSortBy || hasExternalAPIIndicators) {
      return true;
    }

    return false;
  }

  setupEventListeners() {
    // Listen for filter changes
    document.addEventListener('filterChange', (e) => {
      // Only handle if external API is enabled
      if (!this.isExternalApiEnabled()) {
        return;
      }

      // Handle filter updates properly
      Object.entries(e.detail).forEach(([key, values]) => {
        // Handle other filters normally
        if (Array.isArray(values) && values.length === 0) {
          // Remove empty filter arrays
          delete this.currentFilters[key];
        } else if (values) {
          // Set the filter value
          this.currentFilters[key] = values;
        }
      });

      this.currentPage = 1;
      this.updateUrl();
      this.loadData();
      this.updateActiveFilters();
      this.updateSidebarCheckedState();
    });

    // Listen for page changes
    document.addEventListener('pageChange', (e) => {
      // Only handle if external API is enabled
      if (!this.isExternalApiEnabled()) return;

      this.currentPage = e.detail.page;
      this.updateUrl();
      this.loadData();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    // Listen for facet:update events from the existing facets system
    document.addEventListener('facet:update', (e) => {
      // Only handle if external API is enabled
      if (!this.isExternalApiEnabled()) return;

      const url = new URL(e.detail.url);
      const sortBy = url.searchParams.get('sort_by');

      if (sortBy && sortBy !== this.currentSort) {
        this.updateSort(sortBy);
      }
    });

    // Listen for listbox:change events from sort popovers
    document.addEventListener('listbox:change', (e) => {
      // Only handle if external API is enabled
      if (!this.isExternalApiEnabled()) return;

      const sortValue = e.detail.value;
      if (sortValue && sortValue !== this.currentSort) {
        this.updateSort(sortValue);
      }
    });

    // Also listen for direct clicks on sort options as a fallback
    document.addEventListener('click', (e) => {
      if (e.target.closest('.popover-listbox__option')) {
        // Only handle if external API is enabled
        if (!this.isExternalApiEnabled()) return;

        const option = e.target.closest('.popover-listbox__option');
        const sortValue = option.getAttribute('value');
        if (sortValue && sortValue !== this.currentSort) {
          this.updateSort(sortValue);
        }
      }
    });

    // Handle mobile filter drawer button
    this.setupMobileFilterDrawer();
  }

  /**
   * Setup mobile filter drawer functionality
   */
  setupMobileFilterDrawer() {
    // Handle the open filters drawer button
    const openFiltersButton = document.getElementById('open-filters-drawer');
    if (openFiltersButton) {
      openFiltersButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openMobileFilterDrawer();
      });
    }

    // Initialize mobile filters drawer
    this.initializeMobileFiltersDrawer();

    // Update active filters count
    this.updateActiveFiltersCount();
  }

  /**
   * Initialize the mobile filters drawer
   */
  initializeMobileFiltersDrawer() {
    // Find the mobile filters drawer
    const filtersDrawer = document.querySelector('rt-filters-drawer');
    if (filtersDrawer) {
      // Ensure the drawer has proper event listeners
      const closeButton = filtersDrawer.querySelector('.filters-drawer__close');
      const overlay = filtersDrawer.querySelector('.filters-drawer__overlay');
      const applyButton = filtersDrawer.querySelector('.apply-filters');

      if (closeButton) {
        closeButton.addEventListener('click', () => {
          filtersDrawer.close();
        });
      }

      if (overlay) {
        overlay.addEventListener('click', () => {
          filtersDrawer.close();
        });
      }

      if (applyButton) {
        applyButton.addEventListener('click', () => {
          if (window.collectionAPI) {
            window.collectionAPI.applyFilters();
          }
          filtersDrawer.close();
        });
      }
    }
  }

  /**
   * Open the mobile filter drawer
   */
  openMobileFilterDrawer() {
    // Find the mobile filters drawer
    const filtersDrawer = document.querySelector('rt-filters-drawer');
    if (filtersDrawer) {
      filtersDrawer.open();
    } else {
      // Fallback: look for any drawer with filters
      const alternativeDrawer = document.querySelector('.filters-drawer, .facets-drawer');
      if (alternativeDrawer) {
        if (alternativeDrawer.classList.contains('drawer')) {
          // If it's a standard Shopify drawer, trigger it
          const drawerButton = document.querySelector('[aria-controls="facets-drawer"]');
          if (drawerButton) {
            drawerButton.click();
          }
        } else {
          // If it's our custom drawer, try to open it
          if (alternativeDrawer.open) {
            alternativeDrawer.open();
          } else {
            alternativeDrawer.classList.add('is-open');
            document.body.style.overflow = 'hidden';
          }
        }
      }
    }
  }

  /**
   * Update the active filters count in the mobile filter button
   */
  updateActiveFiltersCount() {
    const openFiltersButton = document.getElementById('open-filters-drawer');
    const openFiltersButtonText = openFiltersButton.querySelector('.filter-drawer-toggle__title');
    if (!openFiltersButtonText) return;

    // Use applied filters from API response, fallback to current filters
    const filtersToUse =
      Object.keys(this.appliedFilters).length > 0 ? this.appliedFilters : this.currentFilters;

    // Count active filters from API response (excluding collections, page, and per_page)
    let activeCount = 0;
    Object.entries(filtersToUse).forEach(([key, values]) => {
      if (
        key !== 'collections' &&
        key !== 'page' &&
        key !== 'per_page' &&
        values &&
        values.length > 0
      ) {
        activeCount += Array.isArray(values) ? values.length : 1;
      }
    });

    // Update the button text
    const buttonText = openFiltersButtonText.textContent.trim();
    const baseText = buttonText.replace(/\(\d+\)/, '').trim();

    if (activeCount > 0) {
      openFiltersButtonText.textContent = `${baseText} (${activeCount})`;
    } else {
      openFiltersButtonText.textContent = baseText;
    }
  }

  /**
   * Deprecated - Price filters now use predefined ranges instead of dynamic ranges
   */
  updatePriceRangeFilters() {
    console.warn('updatePriceRangeFilters is deprecated - price filters now use predefined ranges');
  }

  /**
   * Deprecated - Price filters now use predefined ranges instead of dynamic ranges
   */
  static getMaxPriceFromCollection() {
    console.warn(
      'getMaxPriceFromCollection is deprecated - price filters now use predefined ranges'
    );
    return 1000; // Default fallback
  }

  isCustomOrHighPriceVariant(variant) {
    if (!variant) return false;

    const isCustom =
      this.product.tags.includes('Personalize') ||
      this.product.tags.includes('Custom Personalize') ||
      this.product.tags.includes('hide-quick-buy') ||
      this.product.tags.includes('Bridal');
    const isHighPrice = variant.price > 7500000; // Example condition for high price

    return isCustom || isHighPrice;
  }

  isCustom(product) {
    if (!product) return false;
    return (
      product.tags.includes('Personalize') ||
      product.tags.includes('Custom Personalize') ||
      product.tags.includes('hide-quick-buy') ||
      product.tags.includes('Bridal')
    );
  }
}

// Custom Elements

// RT Product List Component
class RTProductList extends HTMLElement {
  constructor() {
    super();
    this.products = [];
    this.contentBlocks = [];
    this.loadContentBlocks();

    // Content blocks can have different page/position/media on mobile,
    // so re-render when crossing the mobile breakpoint
    this.mobileQuery = window.matchMedia('(max-width: 768px)');
    this.mobileQuery.addEventListener('change', () => {
      if (this.products.length) this.render();
    });
  }

  // Merge mobile overrides (mobile_* metaobject fields) over desktop values when on mobile
  getViewportBlock(block) {
    if (!this.mobileQuery?.matches || !block.mobile) return block;
    const overrides = Object.fromEntries(
      Object.entries(block.mobile).filter(([, value]) => value !== '' && value !== null)
    );
    // A mobile image replaces the desktop media entirely (including a desktop video)
    if (overrides.media_url && !overrides.external_video) overrides.external_video = '';
    return { ...block, ...overrides };
  }

  loadContentBlocks() {
    // Load content blocks from the JSON script tag
    const contentBlocksScript = document.querySelector('script[data-large-content-banners]');
    if (contentBlocksScript) {
      try {
        const rawBlocks = JSON.parse(contentBlocksScript.textContent);
        // Store content blocks as-is (position matching handled in render)
        this.contentBlocks = rawBlocks;
      } catch (e) {
        console.warn('Failed to parse content blocks:', e);
        this.contentBlocks = [];
      }
    }
  }

  setProducts(products) {
    this.products = products;
    this.render();
  }

  render() {
    if (!this.products.length) {
      this.innerHTML = '<div class="no-products">No products found</div>';
      return;
    }

    const currentPage = parseInt(this.getCurrentPage());
    const items = [];
    const contentBlocks = this.contentBlocks.map((block) => this.getViewportBlock(block));

    // Check if any filters are applied (excluding collections filter)
    const hasActiveFilters = this.checkIfFiltersApplied();

    // Render products and insert content blocks at appropriate positions
    this.products.forEach((product, index) => {
      const position = index + 1;

      // Add the product card
      items.push(`
        <rt-product-card
          data-position="${position}"
          data-page="${currentPage}"
          data-selected-variant-id="${product.selectedVariantId || ''}"
        >
          <noscript style="display:none">${JSON.stringify(product)}</noscript>
        </rt-product-card>
      `);

      // Only show content blocks if no filters are applied
      if (!hasActiveFilters) {
        // Check if there's a content block that should be inserted AFTER this product
        // Block position is set backward by 1 (e.g., block position 5 appears after product 4)
        // Also check if the block is for the current page
        const matchesSlot = (block) => {
          const blockPosition = parseInt(block.position) || 0;
          const blockPage = parseInt(block.page || 1);
          const matches = blockPosition - 1 === position && blockPage === currentPage;
          if (matches) {
            console.log('Content block matched:', {
              blockPosition,
              position,
              blockPage,
              currentPage,
              block,
            });
          }
          return matches;
        };

        // On mobile, several blocks can share a position (rendered in metafield order);
        // desktop keeps showing only the first match
        const matchedBlocks = this.mobileQuery.matches
          ? contentBlocks.filter(matchesSlot)
          : [contentBlocks.find(matchesSlot)].filter(Boolean);

        matchedBlocks.forEach((contentBlock) => {
          items.push(`
            <rt-content-block
              data-banner='${JSON.stringify(contentBlock)}'
              data-position="${position}"
              data-page="${currentPage}"
            ></rt-content-block>
          `);
        });
      }
    });

    this.innerHTML = items.join('');
  }

  checkIfFiltersApplied() {
    // Check if CollectionAPI has active filters
    if (!window.collectionAPI || !window.collectionAPI.currentFilters) {
      return false;
    }

    let filters = window.collectionAPI.currentFilters;
    // Remove all utm_ parameters from filters
    const filterNames = ['collections', 'colors', 'in_stock', 'price', 'sale'];
    Object.keys(filters).forEach((key) => {
      if (!filterNames.includes(key)) {
        delete filters[key];
      }
    });

    // Check if there are any filters applied (excluding collections, page, per_page, and sort)
    const filterKeys = Object.keys(filters).filter(
      (key) =>
        key !== 'collections' &&
        key !== 'page' &&
        key !== 'per_page' &&
        key !== 'sort' &&
        key !== 'sort_by'
    );

    // Return true if there are any active filters with values
    return filterKeys.some((key) => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined && value !== '';
    });
  }

  getCurrentPage() {
    // Get current page from URL params or context
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('page') || '1';
  }
}

/**
 * Custom card extras fallback (badges + short description)
 * ----------------------------------------------------------------------------
 * Product cards on the collection and search pages are built from the external
 * search API, which does not expose product metafields. When a product payload
 * arrives without its `custom.badges` values, or when the "show short
 * description" setting is on, we read them from Shopify instead, through the
 * `product.badges` alternate template (`/products/<handle>?view=badges`), which
 * renders `{"handle":..,"badges":[..],"description":".."}` with no layout.
 *
 * Lookups are lazy (a card only asks once it is close to the viewport) and are
 * memoized in sessionStorage, so filtering, paginating and moving between the
 * collection and search pages costs at most one request per product per session.
 */
const CUSTOM_BADGE_STORAGE_KEY = 'rt:card-extras:v2';
const CUSTOM_BADGE_MAX_CONCURRENCY = 6;

/** @type {Map<string, {badges: string[], description: string}>} handle -> card extras */
const customBadgeCache = new Map();
/** @type {Map<string, Promise<{badges: string[], description: string}>>} in-flight lookups, keyed by handle */
const customBadgePendingRequests = new Map();
/** @type {Array<() => Promise<void>>} queued lookups waiting for a free slot */
const customBadgeQueue = [];
let customBadgeActiveRequests = 0;

// Warm the in-memory cache from the previous page view of this session
try {
  const stored = JSON.parse(sessionStorage.getItem(CUSTOM_BADGE_STORAGE_KEY) || '{}');
  Object.entries(stored).forEach(([handle, extras]) => {
    if (extras && Array.isArray(extras.badges)) customBadgeCache.set(handle, extras);
  });
} catch {
  // Private browsing or corrupted entry — start from an empty cache
}

function persistCustomBadgeCache() {
  try {
    sessionStorage.setItem(
      CUSTOM_BADGE_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(customBadgeCache))
    );
  } catch {
    // Storage full or unavailable — the in-memory cache still does its job
  }
}

function drainCustomBadgeQueue() {
  while (customBadgeActiveRequests < CUSTOM_BADGE_MAX_CONCURRENCY && customBadgeQueue.length > 0) {
    const job = customBadgeQueue.shift();
    customBadgeActiveRequests += 1;
    job().finally(() => {
      customBadgeActiveRequests -= 1;
      drainCustomBadgeQueue();
    });
  }
}

/**
 * Look up a product's custom badge labels and short description, going through
 * Shopify's `product.badges` view. Never rejects: a failed lookup simply means
 * no extras.
 *
 * @param {string} handle - The product handle
 * @returns {Promise<{badges: string[], description: string}>} Resolves with the card extras (possibly empty)
 */
function fetchCardExtras(handle) {
  if (customBadgeCache.has(handle)) return Promise.resolve(customBadgeCache.get(handle));
  if (customBadgePendingRequests.has(handle)) return customBadgePendingRequests.get(handle);

  const root = window.Shopify?.routes?.root || '/';
  const url = `${root}products/${encodeURIComponent(handle)}?view=badges`;

  const request = new Promise((resolve) => {
    customBadgeQueue.push(async () => {
      let badges = [];
      let description = '';

      try {
        // No explicit Accept header: Shopify treats `Accept: application/json` on a
        // /products/<handle> URL as a request for its native product JSON resource and
        // ignores `?view=`, which would skip the `product.badges` alternate template
        // entirely and return the full product payload (no `badges`/`description` keys).
        const response = await fetch(url);
        if (response.ok) {
          const payload = JSON.parse((await response.text()).trim());
          // A list metafield serializes to an array, a single_line_text_field to a string
          const raw = Array.isArray(payload?.badges)
            ? payload.badges
            : typeof payload?.badges === 'string'
              ? [payload.badges]
              : [];
          badges = raw.map((badge) => String(badge).trim()).filter(Boolean);
          description = typeof payload?.description === 'string' ? payload.description.trim() : '';
        }
      } catch {
        // Network or parse failure — fall through with no extras
      }

      const extras = { badges, description };
      customBadgeCache.set(handle, extras);
      customBadgePendingRequests.delete(handle);
      persistCustomBadgeCache();
      resolve(extras);
    });

    drainCustomBadgeQueue();
  });

  customBadgePendingRequests.set(handle, request);
  return request;
}

// Only look badges up for cards the shopper is about to see
const customBadgeObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            entry.target.loadCustomBadges?.();
          });
        },
        { rootMargin: '400px 0px' }
      )
    : null;

/** @type {Set<HTMLElement>} cards still waiting to be scrolled into view */
const customBadgeIdleCards = new Set();
let customBadgeIdleScheduled = false;

/**
 * Safety net for the observer: a card that never intersects — a background or
 * prerendered tab never paints, so it never reports an intersection — still
 * gets its badges once the page has gone idle.
 *
 * @param {HTMLElement} card - The product card waiting for its badges
 * @returns {void}
 */
function scheduleCustomBadgeIdleLoad(card) {
  customBadgeIdleCards.add(card);
  if (customBadgeIdleScheduled) return;
  customBadgeIdleScheduled = true;

  const run = () => {
    customBadgeIdleScheduled = false;
    const cards = Array.from(customBadgeIdleCards);
    customBadgeIdleCards.clear();
    cards.forEach((pendingCard) => {
      customBadgeObserver?.unobserve(pendingCard);
      pendingCard.loadCustomBadges?.();
    });
  };

  const schedule = () =>
    window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 2000);

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
}

/**
 * Ready to Ship membership (automatic "In Stock" badge)
 * ----------------------------------------------------------------------------
 * A card gets the automatic "In Stock" badge when its product sits in one of
 * the "Ready to Ship collections" theme setting and every variant has
 * inventory on hand. The search API does not expose the collections a product
 * belongs to, so the handles of those collections' products are read once per
 * session from the `collection.rts-handles` alternate template and memoized in
 * sessionStorage, keyed by the configured collections so a settings change
 * invalidates it.
 */
const READY_TO_SHIP_STORAGE_KEY = 'rt:rts-handles:v1';
const READY_TO_SHIP_MAX_PAGES = 20;

/** @type {Set<string>|null} product handles in the Ready to Ship collections, once loaded */
let readyToShipHandles = null;
/** @type {Promise<Set<string>>|null} in-flight lookup shared by every card */
let readyToShipRequest = null;

/**
 * The Ready to Ship collection handles from the theme setting.
 *
 * @returns {string[]} Lowercased collection handles
 */
function getReadyToShipCollections() {
  const raw = window.themeVariables?.settings?.readyToShipCollections;
  if (typeof raw !== 'string') return [];
  return raw
    .split(',')
    .map((handle) => handle.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Whether the page itself is one of the Ready to Ship collections, in which
 * case every product listed on it qualifies without any lookup.
 *
 * @returns {boolean} True on a /collections/<ready-to-ship-handle> page
 */
function isReadyToShipCollectionPage() {
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const index = pathParts.indexOf('collections');
  const handle = index >= 0 ? pathParts[index + 1]?.toLowerCase() : null;
  return Boolean(handle) && handle !== 'all' && getReadyToShipCollections().includes(handle);
}

// Warm the Ready to Ship handles from the previous page view of this session
try {
  const stored = JSON.parse(sessionStorage.getItem(READY_TO_SHIP_STORAGE_KEY) || 'null');
  if (stored?.collections === getReadyToShipCollections().join(',') && Array.isArray(stored.handles)) {
    readyToShipHandles = new Set(stored.handles);
  }
} catch {
  // Private browsing or corrupted entry — look the handles up again
}

/**
 * Read every product handle of one collection through its `rts-handles` view.
 *
 * @param {string} collectionHandle - The collection handle
 * @returns {Promise<{handles: string[], complete: boolean}>} The handles, and whether every page was read
 */
async function fetchCollectionProductHandles(collectionHandle) {
  const root = window.Shopify?.routes?.root || '/';
  const handles = [];

  for (let page = 1; page <= READY_TO_SHIP_MAX_PAGES; page += 1) {
    try {
      const url = `${root}collections/${encodeURIComponent(collectionHandle)}?view=rts-handles&page=${page}`;
      const response = await fetch(url);
      if (!response.ok) return { handles, complete: response.status === 404 };

      const payload = JSON.parse((await response.text()).trim());
      if (Array.isArray(payload?.handles)) handles.push(...payload.handles);
      if (!(payload?.pages > page)) return { handles, complete: true };
    } catch {
      return { handles, complete: false };
    }
  }

  return { handles, complete: true };
}

/**
 * Load the product handles of every Ready to Ship collection. Never rejects:
 * a failed lookup resolves with what could be read, and is not persisted so
 * the next page view retries it.
 *
 * @returns {Promise<Set<string>>} Product handles in the Ready to Ship collections
 */
function loadReadyToShipHandles() {
  if (readyToShipHandles) return Promise.resolve(readyToShipHandles);
  if (readyToShipRequest) return readyToShipRequest;

  const collections = getReadyToShipCollections();
  readyToShipRequest = Promise.all(collections.map(fetchCollectionProductHandles)).then((results) => {
    const handles = new Set(results.flatMap((result) => result.handles));
    readyToShipHandles = handles;

    if (results.every((result) => result.complete)) {
      try {
        sessionStorage.setItem(
          READY_TO_SHIP_STORAGE_KEY,
          JSON.stringify({ collections: collections.join(','), handles: Array.from(handles) })
        );
      } catch {
        // Storage full or unavailable — the in-memory set still does its job
      }
    }

    return handles;
  });

  return readyToShipRequest;
}

// RT Product Card Component
class RTProductCard extends HTMLElement {
  constructor() {
    super();
    this.product = null;
  }

  connectedCallback() {
    const productData = this.querySelector('noscript')?.textContent;
    if (productData) {
      this.product = JSON.parse(productData);
      this.render();
    }
  }

  isCustom(product) {
    if (!product) return false;
    return (
      product.tags.includes('Personalize') ||
      product.tags.includes('Custom Personalize') ||
      product.tags.includes('hide-quick-buy') ||
      product.tags.includes('Bridal')
    );
  }

  getMerchantTemplate() {
    // Get the merchant template from the DOM
    const template = document.getElementById('merchant-product-card-template');
    if (template) {
      return template.innerHTML;
    }
  }

  render() {
    if (!this.product) return;

    const {
      id,
      title,
      price,
      compare_at_price,
      images,
      handle,
      variants,
      price_min,
      price_max,
      ss_price_range,
    } = this.product;
    const selectedVariant = variants && variants.length > 0 ? variants[0] : null;
    const selectedVariantId =
      this.dataset.selectedVariantId || (selectedVariant ? selectedVariant.id : id);

    // Get primary and secondary images
    const primaryImage = images && images.length > 0 ? images[0] : null;
    const secondaryImage = images && images.length > 1 ? images[1] : primaryImage;

    // Generate image URLs and srcsets
    const primaryImageUrl = primaryImage ? primaryImage.src : '';
    const secondaryImageUrl = secondaryImage ? secondaryImage.src : primaryImageUrl;
    const secondaryImageSrcset = secondaryImage ? this.generateSrcset(secondaryImage.src) : '';

    // Format price with "from" logic
    let formattedPrice = this.formatPriceWithRange(
      price,
      price_min,
      price_max,
      ss_price_range,
      variants
    );

    // Generate unique quick buy ID
    const quickBuyId = `${id}-${Date.now()}`;

    // Product JSON for variant selector
    const productJson = JSON.stringify(this.product);

    // Get template and replace placeholders
    let template = this.getMerchantTemplate();

    // If price $0.00, show "Request Price"
    if (parseFloat(price) === 0) {
      formattedPrice = 'Request Price';
    }

    // If product tags include "hide-price", show "Request Price"
    if (
      window?.themeVariables?.settings?.isHidePriceOver &&
      parseFloat(price) >= window?.themeVariables?.settings?.priceOver
    ) {
      formattedPrice = 'Request Price';
    }

    if (this.product.tags && this.product.tags.includes('hide-price')) {
      formattedPrice = 'Request Price';
    }

    // Handle compare at price logic
    let { formattedCompareAtPrice, saleClass, compareAtPriceClass } = this.formatCompareAtPrice(
      price,
      compare_at_price
    );

    if (this.product.tags && this.product.tags.includes('show-price')) {
      formattedPrice = this.formatPrice(price);
    }

    // Apply promo discount to price if any active promo has apply_to_price enabled
    if (formattedPrice !== 'Request Price') {
      const promoDiscount = this.getApplicablePromoDiscount(this.product.tags);
      if (promoDiscount > 0) {
        const originalPrice = parseFloat(price) || 0;
        const discountedPrice = originalPrice * (1 - promoDiscount / 100);
        const isFromPrice = formattedPrice.startsWith('From ');
        const discountedFormatted = this.formatPrice(discountedPrice);

        // If no existing compare-at price is shown, use original price as the strikethrough
        if (!formattedCompareAtPrice || compareAtPriceClass === 'hidden') {
          formattedCompareAtPrice = isFromPrice
            ? `From ${this.formatPrice(originalPrice)}`
            : this.formatPrice(originalPrice);
          compareAtPriceClass = '';
          saleClass = 'text-on-sale';
        }

        formattedPrice = isFromPrice ? `From ${discountedFormatted}` : discountedFormatted;
      }
    }

    const badgesHtml = this.generateBadgesHtml(this.product, selectedVariant);
    // Title remove the double and single quotes to prevent breaking the HTML attributes
    const titleAlt = title.replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const replacements = {
      '[[productHandle]]': handle,
      '[[productTitle]]': title,
      '[[productTitleAlt]]': titleAlt,
      '[[productId]]': id,
      '[[selectedVariantId]]': selectedVariantId,
      '[[primaryImageUrl]]': primaryImageUrl,
      '[[secondaryImageUrl]]': secondaryImageUrl,
      '[[secondaryImageSrcset]]': secondaryImageSrcset,
      '[[productPrice]]': formattedPrice,
      '[[compareAtPrice]]': formattedCompareAtPrice,
      '[[saleClass]]': saleClass,
      '[[compareAtPriceClass]]': compareAtPriceClass,
      '[[quickBuyId]]': quickBuyId,
      '[[product_json]]': productJson,
      '[[position]]': this.getAttribute('data-position') || '1',
      '[[page]]': this.getAttribute('data-page') || '1',
      '[[badgesHtml]]': badgesHtml,
    };

    // Replace all placeholders
    Object.entries(replacements).forEach(([placeholder, value]) => {
      template = template.replace(
        new RegExp(placeholder.replace(/\[/g, '\\[').replace(/\]/g, '\\]'), 'g'),
        value
      );
    });

    this.innerHTML = template;

    this.hydrateCardExtras();
    this.hydrateInStockBadge();

    // Check if tag "hide-quick-buy" is present
    if (this.isCustom(this.product)) {
      const quickBuyButton = this.querySelector('.product-card__quick-add-button');
      if (quickBuyButton) {
        quickBuyButton.style.display = 'none';
      }
    }
  }

  /**
   * Fill in the custom badges and short description that the search API could
   * not provide.
   *
   * The search API indexes the `custom.badges` metafield, so a payload that
   * carries the field is authoritative - an empty list there means "no badges",
   * not "unknown" - and `generateBadgesHtml` has already rendered it. Only cards
   * from a feed that omits the field altogether fall back to asking Shopify.
   * The short description is never indexed by the feed, so it always falls
   * back when the "show short description" setting is on. Either need is
   * enough to trigger one lazy request per product, as it approaches the
   * viewport.
   *
   * @returns {void}
   */
  hydrateCardExtras() {
    const needsBadges = !this.hasCustomBadgesField(this.product);
    const needsDescription =
      window.themeVariables?.settings?.showPlpDescription === true &&
      !this.hasCustomDescriptionField(this.product);

    if (!needsBadges && !needsDescription) return;

    const handle = this.product?.handle;
    if (!handle) return;

    const applyExtras = (extras) => {
      // Bail out if the card was re-rendered with another product in the meantime
      if (!this.isConnected || this.product?.handle !== handle) return;

      if (needsBadges) {
        const figure = this.querySelector('.product-card__figure');
        const labels = this.filterCustomBadges(this.product, extras.badges);
        // A manual "In Stock" badge takes priority over the automatic one
        if (labels.some((label) => this.handleize(label) === 'in-stock')) {
          figure?.querySelector('[data-auto-in-stock]')?.remove();
        }
        if (labels.length && figure) this.appendCustomBadges(figure, labels);
      }

      if (needsDescription && extras.description) {
        this.appendCustomDescription(extras.description);
      }
    };

    if (customBadgeCache.has(handle)) {
      applyExtras(customBadgeCache.get(handle));
      return;
    }

    let requested = false;
    this.loadCustomBadges = () => {
      if (requested) return;
      requested = true;
      fetchCardExtras(handle).then(applyExtras);
    };

    if (customBadgeObserver) {
      customBadgeObserver.observe(this);
      scheduleCustomBadgeIdleLoad(this);
    } else {
      this.loadCustomBadges();
    }
  }

  /**
   * Whether a product payload carries the short description field at all. No
   * known feed indexes it today, so this only guards against a future feed
   * that starts reporting it (in which case we trust it, even if empty).
   *
   * @param {Object} product - The product data object
   * @returns {boolean} True when the feed reported the product's short description
   */
  hasCustomDescriptionField(product) {
    if (!product) return false;
    return [product.clp_short_description, product.metafields?.custom?.clp_short_description].some(
      (value) => value !== undefined && value !== null
    );
  }

  /**
   * Add the automatic "In Stock" badge when the product is in a Ready to Ship
   * collection and every variant has inventory on hand. Mirrors the
   * `ready_to_ship` type of the Liquid `product-badges` snippet.
   *
   * A manual "In Stock" badge from `custom.badges` takes priority: the
   * automatic one is skipped when the card already shows it, and removed if
   * the manual one arrives later through the card extras fallback.
   *
   * @returns {void}
   */
  hydrateInStockBadge() {
    const product = this.product;
    const handle = product?.handle;
    if (!handle || !this.areAllVariantsInStock(product)) return;
    if (this.getCustomBadges(product).some((label) => this.handleize(label) === 'in-stock')) return;

    const apply = () => {
      // Bail out if the card was re-rendered with another product in the meantime
      if (!this.isConnected || this.product?.handle !== handle) return;

      const figure = this.querySelector('.product-card__figure');
      if (!figure || figure.querySelector('.custom-badge--in-stock')) return;

      const label = window.themeVariables?.strings?.inStockBadge || 'In Stock';
      this.appendCustomBadges(figure, [label]);
      figure.querySelector('badge-list .custom-badge--in-stock')?.setAttribute('data-auto-in-stock', '');
    };

    if (isReadyToShipCollectionPage() || readyToShipHandles?.has(handle)) {
      apply();
      return;
    }

    if (readyToShipHandles) return;

    loadReadyToShipHandles().then((handles) => {
      if (handles.has(handle)) apply();
    });
  }

  /**
   * Whether every variant of the product has inventory on hand. Overselling
   * variants (inventory_policy "continue") with no quantity don't count.
   *
   * @param {Object} product - The product data object
   * @returns {boolean} True when the product has variants and all have quantity > 0
   */
  areAllVariantsInStock(product) {
    const variants = product?.variants;
    if (!Array.isArray(variants) || variants.length === 0) return false;
    return variants.every((variant) => Number(variant.inventory_quantity) > 0);
  }

  /**
   * Insert the short description right after the product title, matching the
   * markup the Liquid `product-card` snippet renders for non-API collections.
   *
   * @param {string} description - The short description text
   * @returns {void}
   */
  appendCustomDescription(description) {
    if (this.querySelector('.product-card__description')) return;
    const titleEl = this.querySelector('.product-title');
    if (!titleEl) return;

    const safeDescription = String(description).replace(/[<>]/g, '');
    titleEl.insertAdjacentHTML(
      'afterend',
      `<p class="product-card__description text-subdued line-clamp" style="font-size: 12px; --line-clamp-count: 2">${safeDescription}</p>`
    );
  }

  /**
   * Append custom badges to a card, reusing the badge list already rendered for
   * the sold out / promo badges when there is one.
   *
   * @param {HTMLElement} figure - The `.product-card__figure` element
   * @param {string[]} labels - Badge labels to render
   * @returns {void}
   */
  appendCustomBadges(figure, labels) {
    const markup = labels
      .slice()
      .sort((a, b) => a.localeCompare(b))
      .map((label) => this.renderCustomBadge(label))
      .join('');
    if (!markup) return;

    const existingList = figure.querySelector('badge-list');
    if (existingList) {
      existingList.insertAdjacentHTML('beforeend', markup);
      return;
    }

    const html = `<badge-list class="badge-list badge-list--vertical">${markup}</badge-list>`;
    const media = figure.querySelector('.product-card__media');

    if (media) {
      media.insertAdjacentHTML('beforebegin', html);
    } else {
      figure.insertAdjacentHTML('afterbegin', html);
    }
  }

  /**
   * Normalize the raw "custom badges" value coming from the search API into an
   * array of badge labels.
   *
   * The value is the `custom.badges` product metafield (a list.single_line_text_field),
   * which can reach us in a few different shapes depending on the API/feed:
   * - a real array: ["In-Stock", "Best Seller"]
   * - a JSON string: '["In-Stock","Best Seller"]' (optionally HTML escaped)
   * - a comma separated string: "In-Stock, Best Seller"
   *
   * @param {*} rawBadges - The raw metafield value
   * @returns {string[]} Array of trimmed badge labels
   */
  parseCustomBadges(rawBadges) {
    if (!rawBadges) return [];

    if (Array.isArray(rawBadges)) {
      return rawBadges.map((badge) => String(badge).trim()).filter(Boolean);
    }

    if (typeof rawBadges === 'object') {
      // Metafield objects are sometimes exposed as { value: '[...]' }
      return this.parseCustomBadges(rawBadges.value);
    }

    if (typeof rawBadges !== 'string') return [];

    const unescaped = rawBadges.replace(/&quot;/g, '"').trim();
    if (!unescaped) return [];

    // Try JSON first so labels containing commas survive intact
    if (unescaped.startsWith('[')) {
      try {
        const parsed = JSON.parse(unescaped);
        if (Array.isArray(parsed)) {
          return parsed.map((badge) => String(badge).trim()).filter(Boolean);
        }
      } catch {
        // Fall through to the loose parsing below
      }
    }

    return unescaped
      .replace(/^\[|\]$/g, '')
      .split(',')
      .map((badge) => badge.replace(/^["']|["']$/g, '').trim())
      .filter(Boolean);
  }

  /**
   * Whether a product payload carries the custom badges field at all, under any
   * of the names the different product feeds use. An empty array counts: it is
   * the feed saying this product has no badges.
   *
   * @param {Object} product - The product data object
   * @returns {boolean} True when the feed reported the product's badges
   */
  hasCustomBadgesField(product) {
    if (!product) return false;

    return [
      product.custom_badges,
      product.badges,
      product.mfield_custom_badges,
      product.metafields?.custom?.badges,
    ].some((value) => value !== undefined && value !== null);
  }

  /**
   * Read the custom badge labels from a product payload, tolerating the field
   * names used by the different product feeds.
   *
   * @param {Object} product - The product data object
   * @returns {string[]} Array of badge labels
   */
  getCustomBadges(product) {
    if (!product) return [];

    const rawBadges =
      product.custom_badges ??
      product.badges ??
      product.mfield_custom_badges ??
      product.metafields?.custom?.badges;

    return this.parseCustomBadges(rawBadges);
  }

  /**
   * Drop custom badges that contradict the product's live availability: an
   * "In Stock" badge ("IN STOCK", "in stock", "In-Stock", ...) is hidden when
   * the search API reports `in_stock: false` for the product.
   *
   * @param {Object} product - The product data object
   * @param {string[]} labels - Badge labels to filter
   * @returns {string[]} Badge labels safe to render
   */
  filterCustomBadges(product, labels) {
    if (product?.in_stock !== false) return labels;
    return labels.filter((label) => this.handleize(label) !== 'in-stock');
  }

  /**
   * Turn a badge label into the handleized modifier class used by the theme,
   * e.g. "In-Stock" -> "in-stock", "Extra 15% Off" -> "extra-15-off".
   *
   * @param {string} value - The badge label
   * @returns {string} Handleized value
   */
  handleize(value) {
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Build the markup for a single custom badge, colored from the `product_badge`
   * metaobjects exposed as `window.themeVariables.badges`. Colors are inlined so
   * the badge renders correctly even on pages that never render the Liquid
   * `product-badges` snippet (and therefore never emit its <style> block).
   *
   * @param {string} label - The badge label
   * @returns {string} HTML string for the badge
   */
  renderCustomBadge(label) {
    const themeBadges = window.themeVariables?.badges;
    const badgeSettings = Array.isArray(themeBadges)
      ? themeBadges.find((item) => {
          const nameHandle = item?.name ? this.handleize(item.name) : '';
          return nameHandle !== '' && this.handleize(label).includes(nameHandle);
        })
      : null;

    let style = '';
    if (badgeSettings) {
      if (badgeSettings.text_color) style += `color: ${badgeSettings.text_color};`;
      if (badgeSettings.background_color) style += `background-color: ${badgeSettings.background_color};`;
      if (badgeSettings.custom_style) style += badgeSettings.custom_style;
    }

    const safeLabel = String(label).replace(/[<>]/g, '');

    return `<span class="badge badge--custom custom-badge--${this.handleize(safeLabel)}"${
      style ? ` style="${style}"` : ''
    }>${safeLabel}</span>`;
  }

  /**
   * Generate badges HTML for a product card based on custom badges, availability
   * and sale status.
   *
   * @param {Object} product - The product data object
   * @param {Object|null} variant - The selected/first-available variant
   * @returns {string} HTML string for badge-list element, or empty string if no badges
   */
  generateBadgesHtml(product, variant) {
    let badges = '';

    // Sold out badge: show when no variant can be purchased. A variant is
    // purchasable when it has stock or when it oversells (inventory_policy
    // "continue" — made-to-order styles). `is_available` only reflects that the
    // variant is published, so it can't be used on its own here.
    const isVariantPurchasable = (v) =>
      v.in_stock === true || v.inventory_policy === 'continue' || v.is_sold_out === false;

    const hasAvailableVariant =
      product.variants && product.variants.length > 0
        ? product.variants.some(isVariantPurchasable)
        : product.in_stock === true || product.inventory_policy == 'continue'; // If no variants, check product-level availability
    if (!hasAvailableVariant) {
      badges += '<sold-out-badge class="badge badge--sold-out">Sold Out</sold-out-badge>';
    }

    // Custom badges: the merchandised `custom.badges` metafield, matching what
    // the Liquid `product-badges` snippet renders on the rest of the site.
    this.filterCustomBadges(product, this.getCustomBadges(product))
      .sort((a, b) => a.localeCompare(b))
      .forEach((label) => {
        badges += this.renderCustomBadge(label);
      });

    // // Discount / on-sale badge
    // if (variant && parseFloat(variant.compare_at_price) > parseFloat(variant.price)) {
    //   badges += '<on-sale-badge class="badge badge--on-sale">Sale</on-sale-badge>';
    // } else if (!variant && parseFloat(product.compare_at_price) > parseFloat(product.price)) {
    //   badges += '<on-sale-badge class="badge badge--on-sale">Sale</on-sale-badge>';
    // }

    // Promo badge: show when a promo has a badge_tag and the product has a matching tag prefixed with "badge:"
    if (window.globalPromoMessages && Array.isArray(window.globalPromoMessages) && product.tags) {
      window.globalPromoMessages.forEach((promo) => {
        if (!promo.badge_tag) return;
        const matchingTag = product.tags.find((tag) => tag === promo.badge_tag);
        if (matchingTag) {
          const badgeMessage = matchingTag.replace(/^badge:/, '');
          const badgeColor = promo.badge_color || '#FE3981';
          badges += `<on-sale-badge class="badge badge--on-sale" style="background-color: ${badgeColor};">${badgeMessage}</on-sale-badge>`;
        }
      });
    }

    if (!badges) return '';
    return `<badge-list class="badge-list badge-list--vertical">${badges}</badge-list>`;
  }

  /**
   * Returns the highest applicable promo discount percentage for a product,
   * checking page type, date range, and product tag filters from window.globalPromoMessages.
   *
   * @param {string[]} productTags - The product's tags array
   * @returns {number} Discount percentage (0 if none applies)
   */
  getApplicablePromoDiscount(productTags) {
    if (!window.globalPromoMessages || !Array.isArray(window.globalPromoMessages)) return 0;

    const path = window.location.pathname;
    const currentPage = path.includes('/products/')
      ? 'product'
      : path.includes('/collections/')
        ? 'collection'
        : 'other';

    const currentDate = new Date();
    let maxDiscount = 0;

    window.globalPromoMessages.forEach((promo) => {
      if (!promo.apply_to_price || Number(promo.discount_percentage) <= 0) return;

      if (promo.message_position !== 'all' && promo.message_position !== currentPage) return;

      // Check start date
      if (promo.start_date) {
        const startDate = new Date(promo.start_date);
        if (
          !promo.start_date.includes(':') &&
          !promo.start_date.toLowerCase().includes('am') &&
          !promo.start_date.toLowerCase().includes('pm')
        ) {
          startDate.setHours(0, 0, 0, 0);
        }
        if (currentDate < startDate) return;
      }

      // Check end date
      if (promo.end_date) {
        const endDate = new Date(promo.end_date);
        if (
          !promo.end_date.includes(':') &&
          !promo.end_date.toLowerCase().includes('am') &&
          !promo.end_date.toLowerCase().includes('pm')
        ) {
          endDate.setHours(23, 59, 59, 999);
        }
        if (currentDate > endDate) return;
      }

      // Check product tag filters
      if (
        promo.product_exclude_tag &&
        productTags &&
        productTags.includes(promo.product_exclude_tag)
      )
        return;

      if (promo.product_tag && (!productTags || !productTags.includes(promo.product_tag))) return;

      maxDiscount = Math.max(maxDiscount, Number(promo.discount_percentage));
    });

    return maxDiscount;
  }

  generateSrcset(imageUrl) {
    if (!imageUrl) return '';

    // Generate srcset for different image sizes
    const sizes = [300, 600, 900, 1200, 1500];
    return sizes
      .map((size) => {
        const resizedUrl = imageUrl.replace(/\.(jpg|jpeg|png|webp)$/i, `_${size}x.$1`);
        return `${resizedUrl} ${size}w`;
      })
      .join(', ');
  }

  formatPrice(price) {
    const numericPrice = typeof price === 'number' ? price : parseFloat(price);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numericPrice);
  }

  formatPriceWithRange(price, priceMin, priceMax, ssPriceRange, variants) {
    // Check if we should show "from" pricing
    const shouldShowFrom = this.shouldShowFromPricing(
      price,
      priceMin,
      priceMax,
      ssPriceRange,
      variants
    );

    const formattedPrice = this.formatPrice(price);

    if (shouldShowFrom) {
      return `From ${formattedPrice}`;
    }

    return formattedPrice;
  }

  formatCompareAtPrice(price, compareAtPrice) {
    const currentPrice = parseFloat(price) || 0;
    const comparePrice = parseFloat(compareAtPrice) || 0;

    let saleClass = '';
    let compareAtPriceClass = '';
    let formattedCompareAtPrice = '';

    // Only process if compare_at_price exists and is greater than 0
    if (comparePrice > 0) {
      formattedCompareAtPrice = this.formatPrice(comparePrice);

      // If compare_at_price is greater than price, add text-on-sale class to sale-price
      if (comparePrice > currentPrice) {
        saleClass = 'text-on-sale';
      } else {
        // If compare_at_price is lower or equal to price, hide the compare-at-price div
        compareAtPriceClass = 'hidden';
      }
    } else {
      // If no compare_at_price or it's 0, hide the compare-at-price div
      compareAtPriceClass = 'hidden';
    }

    return {
      formattedCompareAtPrice,
      saleClass,
      compareAtPriceClass,
    };
  }

  shouldShowFromPricing(price, priceMin, priceMax, ssPriceRange, variants) {
    // Check if product has the plp_from_price tag
    if (this.product && this.product.tags && this.product.tags.includes('plp_from_price')) {
      return true;
    }

    // Check ss_price_range flag first (matches ss-template.js logic)
    if (ssPriceRange) {
      return true;
    }

    // Check if there are multiple variants with different prices
    if (variants && variants.length > 1) {
      const prices = variants.map((v) => parseFloat(v.price || 0)).filter((p) => p > 0);
      if (prices.length > 1) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        // Show "from" if there's a price range
        if (maxPrice > minPrice) {
          return true;
        }
      }
    }

    // Check if explicit price_min and price_max are provided and different
    if (priceMin !== undefined && priceMax !== undefined) {
      const min = parseFloat(priceMin);
      const max = parseFloat(priceMax);
      if (!isNaN(min) && !isNaN(max) && max > min) {
        return true;
      }
    }

    return false;
  }
}

// RT Filters Component
class RTFilters extends HTMLElement {
  constructor() {
    super();
    this.filters = [];
    this.activeFilters = {};
    this.showAllFilters = true; // Track whether to show all filters or just first 3
  }

  setFilters(filters) {
    this.filters = filters;
    this.render();

    // Update checked states after rendering
    setTimeout(() => {
      this.updateAllFilterStates();
    }, 0);
  }

  updateAllFilterStates() {
    // Update all filter components to reflect current state
    this.querySelectorAll('rt-filter').forEach((filterElement) => {
      if (filterElement.updateCheckedState) {
        filterElement.updateCheckedState();
      }
      if (filterElement.checkFilterVisibility) {
        filterElement.checkFilterVisibility();
      }
    });
  }

  render() {
    if (!this.filters.length) {
      this.innerHTML = '<div class="no-filters">No filters available</div>';
      return;
    }

    // Check if we're in topbar mode
    const isTopbarMode = this.closest('.collection-toolbar__topbar-filters');

    if (isTopbarMode) {
      // Render as horizontal dropdown filters for topbar
      const visibleFilters = this.showAllFilters ? this.filters : this.filters.slice(0, 4);
      const buttonText = this.showAllFilters ? 'View less -' : 'View all +';

      this.innerHTML = `
        <div class="filter-dropdowns">
          <span class="filter-label">Filter By:</span>
          ${visibleFilters
            .map(
              (filter) => `
            <rt-filter class="collection-filters__item--${filter.field_name}" data-filter='${JSON.stringify(filter)}'></rt-filter>
          `
            )
            .join('')}
          ${this.filters.length > 4 ? `<button class="view-all-btn" style="${this.showAllFilters ? 'display: none' : 'display: block'}">${buttonText}</button>` : ''}
        </div>
      `;

      // Add click handler for the view all/less button
      const viewAllBtn = this.querySelector('.view-all-btn');
      if (viewAllBtn) {
        viewAllBtn.addEventListener('click', () => {
          this.showAllFilters = !this.showAllFilters;
          this.render();
        });
      }
    } else {
      // Render as vertical accordion for sidebar/drawer
      this.innerHTML = `
        <div class="filters">
          <div class="filters__header">
            <h2>Filter Products</h2>
            ${
              Object.keys(this.activeFilters).length > 0
                ? `
              <button class="filters__clear" onclick="this.parentElement.parentElement.parentElement.clearAllFilters()">
                Clear All
              </button>
            `
                : ''
            }
          </div>
          ${this.filters
            .map(
              (filter) => `
            <rt-filter class="collection-filters__item--${filter.field_name}" data-filter='${JSON.stringify(filter)}'></rt-filter>
          `
            )
            .join('')}
        </div>
      `;
    }
  }

  clearAllFilters() {
    this.activeFilters = {};
    document.dispatchEvent(
      new CustomEvent('filterChange', {
        detail: {},
        bubbles: true,
      })
    );
  }
}

// RT Filter Component
class RTFilter extends HTMLElement {
  constructor() {
    super();
    this.filter = null;
    this.isExpanded = false; // Start collapsed by default
    this.isDropdownMode = false;
  }

  connectedCallback() {
    const filterData = this.getAttribute('data-filter');
    if (filterData) {
      this.filter = JSON.parse(filterData);
      this.checkIfShouldBeExpanded();
      this.detectLayoutMode();
      this.render();

      // Listen for global filter changes to update this filter's state
      this.setupGlobalFilterListener();
    }
  }

  setupGlobalFilterListener() {
    // Listen for filter changes to update filter state
    this.filterChangeHandler = () => {
      // Small delay to ensure the filter state has been updated
      setTimeout(() => {
        this.updateCheckedState();
        this.checkFilterVisibility();
        // Ensure dropdown stays closed when filters are updated
        if (this.isDropdownMode && this.isExpanded) {
          this.closeDropdown();
        }
      }, 50);
    };

    document.addEventListener('filterChange', this.filterChangeHandler);
  }

  detectLayoutMode() {
    // Check if we're in topbar mode by looking at parent element
    const topbarContainer = this.closest('.collection-toolbar__topbar-filters');
    this.isDropdownMode = !!topbarContainer;
  }

  checkIfShouldBeExpanded() {
    // Check if we're in a mobile drawer context
    const isInDrawer = this.closest('rt-filters-drawer');

    if (isInDrawer) {
      // Always start collapsed in mobile drawer
      this.isExpanded = false;
    } else {
      // For desktop: sidebar layout expands by default, dropdown layout stays collapsed
      this.isExpanded = !this.isDropdownMode;
    }
  }

  render() {
    if (!this.filter) return;

    const { id, label, type, values, is_swatch, field_name } = this.filter;

    if (this.isDropdownMode) {
      // Render as dropdown for topbar layout
      this.innerHTML = `
        <div class="filter-dropdown collection-filters__item--${field_name}" data-filter-id="${id}">
          <button type="button" class="filter-dropdown__toggle" aria-expanded="${this.isExpanded}" aria-label="Filter by ${label}">
            ${label}
            <svg width="12" height="8" aria-label="Dropdown arrow">
              <path d="M1 1l5 5 5-5" stroke="currentColor" fill="none" stroke-width="2"/>
            </svg>
          </button>
          <div class="filter-dropdown__content ${this.isExpanded ? 'show' : ''}">
            <div class="filter-dropdown__title">
              ${label}
            </div>
            <div class="filter-dropdown__options--group">
              ${this.renderDropdownValues(type, values, id, is_swatch)}
            </div>
            <div class="filter-dropdown__submit--button">
              <button data-button-id="${id}">Refine search</button>
            </div>
          </div>
        </div>
      `;

      // Add event listener after rendering
      const toggleButton = this.querySelector('.filter-dropdown__toggle');
      if (toggleButton) {
        toggleButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleDropdown(id);
        });
      }

      // Prevent dropdown content from closing when clicking inside it
      const dropdownContent = this.querySelector('.filter-dropdown__content');
      if (dropdownContent) {
        dropdownContent.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      }

      // Add event listener for the "Refine search" button
      const submitButton = this.querySelector('.filter-dropdown__submit--button button');
      if (submitButton) {
        submitButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.applyDropdownFilters(id);
        });
      }
    } else {
      // Render as accordion for sidebar/drawer layout
      this.innerHTML = `
        <div class="filter collection-filters__item--${field_name}" data-filter-id="${id}">
          <button class="filter__toggle h6" onclick="this.closest('rt-filter').toggleFilter('${id}')">
            <span class="filter__label">${label}</span>
            <svg class="filter__icon ${this.isExpanded ? 'expanded' : ''}" width="12" height="8">
              <path d="M1 1l5 5 5-5" stroke="currentColor" fill="none" stroke-width="2"/>
            </svg>
          </button>
            <div class="filter__content ${this.isExpanded ? 'expanded' : ''}">
              ${this.renderFilterValues(type, values, id, is_swatch)}
            </div>
        </div>
      `;
    }

    // Update checked state after rendering
    this.updateCheckedState();

    // Check if filter should be hidden (no inputs)
    this.checkFilterVisibility();

    // Setup dropdown click outside handler for dropdown mode
    if (this.isDropdownMode) {
      this.setupDropdownHandlers();
      // Ensure dropdown is closed after rendering if it was closed before
      if (!this.isExpanded) {
        this.closeDropdown();
      }
    }
  }

  renderDropdownValues(type, values, id, is_swatch = false) {
    switch (type) {
      case 'text':
      case 'list':
        let result = '';
        values.forEach((value, index) => {
          const opt_label = value.label.toLowerCase();
          let swatchImgSrc = null;
          let swatchStyle = '';

          if (is_swatch) {
            swatchImgSrc =
              window.swatch_config?.[opt_label] ||
              `https://${window.shop_domain}/cdn/shop/files/${opt_label.replaceAll(' ', '-')}.png`;
            swatchStyle = swatchImgSrc ? `style="--swatch-image: url(${swatchImgSrc})"` : '';
          }

          // Add filter-columns div every 6 items
          if (index % 6 === 0) {
            if (index > 0) {
              result += '</div>'; // Close previous column
            }
            result += '<div class="filter-columns">';
          }

          result += `
            <label class="filter-dropdown__option ${is_swatch ? 'filter-dropdown__option--swatch' : ''}" ${swatchStyle}>
              <input type="checkbox" name="${id}" value="${value.value}">
              ${is_swatch ? `<span class="swatch-circle"></span>` : ''}
              <span>${value.label}</span>
            </label>
          `;
        });

        // Close the last column if we have any items
        if (values.length > 0) {
          result += '</div>';
        }

        return result;
      case 'price':
      case 'range':
        // Deprecated - price filters now use list type with predefined ranges
        return '';
      default:
        return '';
    }
  }

  setupDropdownHandlers() {
    // Clean up any existing handlers first
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }

    // Add a unique identifier for this filter dropdown
    this.dropdownId = `dropdown-${Math.random().toString(36).substr(2, 9)}`;

    // Store reference to the event handler so we can remove it later
    this.clickOutsideHandler = (event) => {
      // Check if the click is outside this filter component
      if (!this.contains(event.target) && this.isExpanded) {
        this.closeDropdown();
      }
    };

    // Add click outside handler with a slight delay to avoid immediate closure
    setTimeout(() => {
      document.addEventListener('click', this.clickOutsideHandler);
    }, 10);
  }

  toggleDropdown(filterId) {
    // Close all other dropdowns first
    document.querySelectorAll('rt-filter').forEach((filter) => {
      if (filter !== this && filter.isDropdownMode && filter.isExpanded) {
        filter.closeDropdown();
      }
    });

    // Toggle current dropdown
    this.isExpanded = !this.isExpanded;
    this.updateDropdownState();
  }

  updateDropdownState() {
    const content = this.querySelector('.filter-dropdown__content');
    const toggle = this.querySelector('.filter-dropdown__toggle');

    if (content && toggle) {
      if (this.isExpanded) {
        content.classList.add('show');
        toggle.setAttribute('aria-expanded', 'true');
      } else {
        content.classList.remove('show');
        toggle.setAttribute('aria-expanded', 'false');
      }
    }
  }

  closeDropdown() {
    if (this.isExpanded) {
      this.isExpanded = false;
      this.updateDropdownState();
    }
  }

  checkFilterVisibility() {
    if (!this.filter) {
      this.style.display = 'none';
      return;
    }

    // Collections filter is now allowed to be shown in the filter interface
    // Commenting out the restriction that was preventing collections from being displayed
    // if (this.filter.id === 'collections') {
    //   this.style.display = 'none';
    //   return;
    // }

    let shouldShow = false;

    // For price filters, always show them since they now use predefined ranges
    if (this.filter.id === 'price') {
      shouldShow = true;
    } else {
      // For other filter types, check for inputs and options
      if (this.isDropdownMode) {
        // Check if dropdown content has any input elements
        const content = this.querySelector('.filter-dropdown__content');
        if (content) {
          const inputs = content.querySelectorAll(
            'input[type="checkbox"], input[type="radio"], input[type="range"]'
          );
          shouldShow = inputs.length > 0;

          // For list filters, also check if there are actual options
          if (shouldShow && this.filter.type === 'list' && this.filter.values) {
            shouldShow = this.filter.values.length > 0;
          }
        }
      } else {
        // For sidebar/drawer mode
        const content = this.querySelector('.filter__content');
        if (content) {
          const inputs = content.querySelectorAll(
            'input[type="checkbox"], input[type="radio"], input[type="range"]'
          );
          shouldShow = inputs.length > 0;

          // For list filters, also check if there are actual options
          if (shouldShow && this.filter.type === 'list' && this.filter.values) {
            shouldShow = this.filter.values.length > 0;
          }
        }
      }
    }

    // Apply visibility
    this.style.display = shouldShow ? 'block' : 'none';
  }

  disconnectedCallback() {
    // Clean up event listeners when component is removed
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
      this.clickOutsideHandler = null;
    }

    if (this.filterChangeHandler) {
      document.removeEventListener('filterChange', this.filterChangeHandler);
      this.filterChangeHandler = null;
    }
  }

  updateCheckedState() {
    // Get current filters from the API instance
    const currentFilters = window.collectionAPI?.currentFilters || {};
    const filterId = this.filter?.id;

    if (filterId && currentFilters[filterId]) {
      const activeValues = currentFilters[filterId];

      if (filterId === 'price') {
        // Special handling for price ranges - check all ranges that fall within the combined range
        this.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
          const rangeValue = checkbox.value; // e.g., "500-1500"
          checkbox.checked = this.isPriceRangeWithinFilter(rangeValue, activeValues);
        });
      } else {
        // For all other filters, use exact match
        this.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
          checkbox.checked = activeValues.includes(checkbox.value);
        });
      }
    } else {
      // Clear all selections if no active filters for this filter type
      this.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
        input.checked = false;
      });
    }

    // Ensure dropdown stays closed when updating checked state
    if (this.isDropdownMode && this.isExpanded) {
      this.closeDropdown();
    }
  }

  // Helper method to determine if a price range should be checked based on the active filter
  isPriceRangeWithinFilter(rangeValue, activeFilterValues) {
    if (
      !activeFilterValues ||
      !Array.isArray(activeFilterValues) ||
      activeFilterValues.length === 0
    ) {
      return false;
    }

    // Parse the checkbox range value (e.g., "500-1500")
    const rangeParts = rangeValue.split('-');
    const rangeMin = parseFloat(rangeParts[0]) || 0;
    const rangeMax = parseFloat(rangeParts[1]) || Number.MAX_VALUE;

    // Find the overall min and max from all active filter values
    let filterMin = null;
    let filterMax = null;

    activeFilterValues.forEach((filterValue) => {
      const filterParts = filterValue.split('-');
      const filterRangeMin = parseFloat(filterParts[0]) || 0;
      const filterRangeMax = parseFloat(filterParts[1]) || Number.MAX_VALUE;

      if (filterMin === null || filterRangeMin < filterMin) {
        filterMin = filterRangeMin;
      }
      if (filterMax === null || filterRangeMax > filterMax) {
        filterMax = filterRangeMax;
      }
    });

    // Check if this range is completely contained within or exactly matches the filter range
    // A range is selected only if it's completely within the filter boundaries
    const isCompletelyWithin = rangeMin >= filterMin && rangeMax <= filterMax;

    return isCompletelyWithin;
  }

  formatPriceByCurrency(price, currency = 'USD', locale = 'en-US') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(price);
  }

  renderFilterValues(type, values, filterId, is_swatch = false) {
    switch (type) {
      case 'list':
        // Use checkboxes for all filters including collections and price ranges to support multiple selection
        const inputType = 'checkbox';
        const inputName = filterId === 'collections' ? 'collection-filter' : '';
        return `
          <div class="filter__list">
            ${values
              .map((value) => {
                const opt_label = value.label.toLowerCase();
                let swatchImgSrc = null;
                let swatchStyle = '';

                if (is_swatch) {
                  swatchImgSrc =
                    window.swatch_config?.[opt_label] ||
                    `https://${window.shop_domain}/cdn/shop/files/${opt_label.replaceAll(' ', '-')}.png`;
                  swatchStyle = swatchImgSrc ? `--swatch-image: url(${swatchImgSrc})` : '';
                }

                return `
              <label class="filter__option ${is_swatch ? 'filter__option--swatch' : ''}" style="${swatchStyle}">
                <input
                  type="${inputType}"
                  ${inputName ? `name="${inputName}"` : ''}
                  value="${value.value}"
                  onchange="this.closest('rt-filter').handleFilterChange('${filterId}', this)"
                />
                ${is_swatch ? `<span class="swatch-circle"></span>` : ''}
                <span class="filter__option-label">${value.label}</span>
              </label>
            `;
              })
              .join('')}
          </div>
        `;
      case 'range':
        // Deprecated - price filters now use list type with predefined ranges
        if (filterId == 'price') {
          console.warn('Price filter should use type "list" with predefined ranges, not "range"');
          return '';
        }
      default:
        return '';
    }
  }

  toggleFilter(filterId) {
    this.isExpanded = !this.isExpanded;
    this.render();
  }

  handleFilterChange(filterId, input) {
    // Check if we're in a mobile drawer context
    const isInDrawer = this.closest('rt-filters-drawer');

    // Get filter values regardless of context
    let filterValues = [];

    if (filterId === 'collections') {
      // For collections, support both radio buttons (single selection) and checkboxes (multiple selection)
      const checkedInputs = this.querySelectorAll(`input:checked`);

      if (checkedInputs.length > 0) {
        // Check if we have checkboxes (multiple selection) or radio buttons (single selection)
        const hasCheckboxes = this.querySelector(`input[type="checkbox"]`);

        if (hasCheckboxes) {
          // Multiple selection with checkboxes - get all checked values
          filterValues = Array.from(checkedInputs).map((input) => input.value);
        } else {
          // Single selection with radio buttons - get the checked radio
          const checkedRadio = this.querySelector(`input[type="radio"]:checked`);
          if (checkedRadio) {
            filterValues = [checkedRadio.value];
          }
        }
      }
    } else {
      // For all other filters (checkboxes), get all checked checkboxes
      const checkedValues = Array.from(this.querySelectorAll(`input[type="checkbox"]:checked`)).map(
        (cb) => cb.value
      );
      filterValues = checkedValues;
    }

    if (isInDrawer) {
      // In mobile drawer, dispatch filterChange event for the drawer to capture
      // but don't apply immediately
      document.dispatchEvent(
        new CustomEvent('filterChange', {
          detail: { [filterId]: filterValues },
          bubbles: true,
        })
      );
      return;
    }

    // For sidebar/desktop, apply filters immediately
    document.dispatchEvent(
      new CustomEvent('filterChange', {
        detail: { [filterId]: filterValues },
        bubbles: true,
      })
    );
  }

  // Deprecated - price filters now use checkbox lists instead of range sliders
  handlePriceChange(filterId) {
    console.warn('handlePriceChange is deprecated - price filters now use checkbox lists');
  }

  // Deprecated - price filters now use checkbox lists instead of range sliders
  handleRangeSliderChange(filterId) {
    console.warn('handleRangeSliderChange is deprecated - price filters now use checkbox lists');
  }

  applyDropdownFilters(filterId) {
    let filterValues = [];

    // For all filters, get all checked checkbox values
    const checkedValues = Array.from(this.querySelectorAll(`input[type="checkbox"]:checked`)).map(
      (cb) => cb.value
    );
    filterValues = checkedValues;

    // Close the dropdown after applying filters
    this.closeDropdown();

    // Dispatch the filter change event
    document.dispatchEvent(
      new CustomEvent('filterChange', {
        detail: { [filterId]: filterValues },
        bubbles: true,
      })
    );
  }

  // Deprecated - price filters now use checkbox lists instead of range sliders
  applyStoredSliderValues(filterId) {
    console.warn('applyStoredSliderValues is deprecated - price filters now use checkbox lists');
  }

  // Deprecated - price filters now use checkbox lists instead of range sliders
  setupRangeSliderListeners() {
    console.warn('setupRangeSliderListeners is deprecated - price filters now use checkbox lists');
  }
}

// RT Paginate Component
class RTPaginate extends HTMLElement {
  constructor() {
    super();
    this.pagination = null;
  }

  setPagination(pagination) {
    this.pagination = pagination;
    this.render();
  }

  render() {
    if (!this.pagination || this.pagination.total_pages <= 1) {
      this.innerHTML = '';
      return;
    }

    const { current_page, total_pages, has_previous, has_next } = this.pagination;

    this.innerHTML = `
      <nav class="pagination" aria-label="Products pagination" role="navigation">
        ${
          has_previous
            ? `
          <a href="#" class="pagination__link" onclick="event.preventDefault(); this.closest('rt-paginate').changePage(${current_page - 1})" title="Go to page ${current_page - 1}">
            <svg width="8" height="12" aria-hidden="true"><path d="M7 1L2 6l5 5" stroke="currentColor" fill="none" stroke-width="2"/></svg>
            <span class="visually-hidden">Previous page</span>
          </a>
        `
            : `
          <span class="pagination__link pagination__link--disabled">
            <svg width="8" height="12" aria-hidden="true"><path d="M7 1L2 6l5 5" stroke="currentColor" fill="none" stroke-width="2"/></svg>
            <span class="visually-hidden">Previous page (disabled)</span>
          </span>
        `
        }

        ${this.renderPageNumbers(current_page, total_pages)}

        ${
          has_next
            ? `
          <a href="#" class="pagination__link" onclick="event.preventDefault(); this.closest('rt-paginate').changePage(${current_page + 1})" title="Go to page ${current_page + 1}">
            <span class="visually-hidden">Next page</span>
            <svg width="8" height="12" aria-hidden="true"><path d="M1 1l5 5-5 5" stroke="currentColor" fill="none" stroke-width="2"/></svg>
          </a>
        `
            : `
          <span class="pagination__link pagination__link--disabled">
            <span class="visually-hidden">Next page (disabled)</span>
            <svg width="8" height="12" aria-hidden="true"><path d="M1 1l5 5-5 5" stroke="currentColor" fill="none" stroke-width="2"/></svg>
          </span>
        `
        }
      </nav>
    `;
  }

  renderPageNumbers(current, total) {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, current - Math.floor(showPages / 2));
    let endPage = Math.min(total, startPage + showPages - 1);

    if (endPage - startPage < showPages - 1) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(`
        <a href="#" class="pagination__link" onclick="event.preventDefault(); this.closest('rt-paginate').changePage(1)" title="Go to page 1">
          1
        </a>
      `);

      if (startPage > 2) {
        pages.push(`<span class="pagination__link pagination__link--ellipsis">…</span>`);
      }
    }

    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      if (i === current) {
        pages.push(`
          <span class="pagination__link" aria-current="page" title="Current page, page ${i}">
            ${i}
          </span>
        `);
      } else {
        pages.push(`
          <a href="#" class="pagination__link" onclick="event.preventDefault(); this.closest('rt-paginate').changePage(${i})" title="Go to page ${i}">
            ${i}
          </a>
        `);
      }
    }

    // Add last page and ellipsis if needed
    if (endPage < total) {
      if (endPage < total - 1) {
        pages.push(`<span class="pagination__link pagination__link--ellipsis">…</span>`);
      }

      pages.push(`
        <a href="#" class="pagination__link" onclick="event.preventDefault(); this.closest('rt-paginate').changePage(${total})" title="Go to page ${total}">
          ${total}
        </a>
      `);
    }

    return pages.join('');
  }

  changePage(page) {
    document.dispatchEvent(
      new CustomEvent('pageChange', {
        detail: { page },
        bubbles: true,
      })
    );
  }
}

// Custom element for sorting
class RTSortBy extends HTMLElement {
  constructor() {
    super();
    this.initializationAttempts = 0;
    this.maxAttempts = 10; // Maximum 10 attempts (5 seconds total)
  }

  connectedCallback() {
    // Wait a bit for CollectionAPI to be ready
    setTimeout(() => {
      this.initialize();
    }, 100);
  }

  initialize() {
    // Only initialize if external API is enabled
    if (!window.collectionAPI || !window.collectionAPI.isExternalApiEnabled()) {
      this.initializationAttempts++;

      if (this.initializationAttempts >= this.maxAttempts) {
        return;
      }

      // Retry after a delay in case CollectionAPI is still initializing
      setTimeout(() => {
        this.initialize();
      }, 500);
      return;
    }

    // Check if we're in a facets-sort-popover context
    const isInPopover = this.closest('facets-sort-popover');

    if (isInPopover) {
      // If we're inside a facets-sort-popover, we don't need to render our own UI
      // The popover will handle the UI, we just need to listen for changes
      this.setupPopoverListener();
    } else {
      // Standalone sort component
      this.renderStandalone();
    }
  }

  setupPopoverListener() {
    // Listen for sort changes from the facets-sort-popover
    this.addEventListener('listbox:change', (event) => {
      const sortValue = event.detail.value;
      if (window.collectionAPI && window.collectionAPI.isExternalApiEnabled()) {
        // Prevent the default facets system from handling this
        event.stopPropagation();
        event.preventDefault();
        window.collectionAPI.updateSort(sortValue);
      } else {
        console.warn('RTSortBy: External API not enabled, letting standard facets handle');
      }
    });

    // Also listen for the facet:update event to update our state
    document.addEventListener('facet:update', (event) => {
      // Only handle facet:update if external API is NOT enabled
      // (this is for standard Shopify facets)
      if (!window.collectionAPI || !window.collectionAPI.isExternalApiEnabled()) {
        const url = new URL(event.detail.url);
        const sortValue = url.searchParams.get('sort_by');
        this.updateSort(sortValue);
      }
    });

    // Add direct click handler for sort options as fallback
    document.addEventListener('click', (event) => {
      if (event.target.closest('.popover-listbox__option')) {
        const option = event.target.closest('.popover-listbox__option');
        const sortValue = option.getAttribute('value');

        if (sortValue && window.collectionAPI && window.collectionAPI.isExternalApiEnabled()) {
          event.preventDefault();
          event.stopPropagation();
          window.collectionAPI.updateSort(sortValue);
        } else {
        }
      }
    });
  }

  renderStandalone() {
    this.innerHTML = `
      <select class="sort-select">
        <option value="">Sort by</option>
        <option value="featured">Featured</option>
        <option value="title_asc">Alphabetically, A–Z</option>
        <option value="title_desc">Alphabetically, Z–A</option>
        <option value="price_asc">Price, low to high</option>
        <option value="price_desc">Price, high to low</option>
        <option value="created_at_asc">Date, old to new</option>
        <option value="created_at_desc">Date, new to old</option>
      </select>
    `;

    const select = this.querySelector('.sort-select');
    select.addEventListener('change', (e) => {
      if (window.collectionAPI && window.collectionAPI.isExternalApiEnabled()) {
        window.collectionAPI.updateSort(e.target.value);
      }
    });
  }

  updateSort(sortValue) {
    // Update the select element if it exists
    const select = this.querySelector('.sort-select');
    if (select) {
      select.value = sortValue || '';
    }

    // Update the popover listbox if we're in a popover context
    const popover = this.closest('facets-sort-popover');
    if (popover) {
      const listbox = popover.querySelector('x-listbox');
      if (listbox) {
        // Update the selected option in the listbox
        const options = listbox.querySelectorAll('.popover-listbox__option');
        options.forEach((option) => {
          if (option.value === sortValue) {
            option.setAttribute('aria-selected', 'true');
          } else {
            option.removeAttribute('aria-selected');
          }
        });
      }
    }
  }
}

// Custom element for active filters display
class RTActiveFilters extends HTMLElement {
  connectedCallback() {
    this.className = 'active-filters';
  }

  transformFilterValue(value, key) {
    // split by :
    if (value.includes('sale_filter:')) {
      return value.replace('sale_filter:', 'Sale:').trim();
    }
    if (key === 'collections') {
      return value
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
    }
    const parts = value.split(':');
    return parts.length > 1 ? parts[1].trim() : value;
  }

  updateActiveFilters(filters) {
    const activeFilters = [];

    Object.entries(filters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        if (key == 'collections' && values.length == 1) {
          return; // Skip single collection filters
        }
        values.forEach((value) => {
          activeFilters.push({ key, value });
        });
      }
    });

    if (activeFilters.length === 0) {
      this.innerHTML = '';
      return;
    }

    this.innerHTML = `
      <div class="active-filters__list">
        ${activeFilters
          .map(
            (filter) => `
          <span class="active-filter" data-remove-wrapper data-key="${filter.key}" data-value="${filter.value}">
            ${this.transformFilterValue(filter.value, filter.key)}
            <button 
              type="button"
              class="remove-filter"
              data-key="${filter.key}"
              data-value="${filter.value}"
              aria-label="Remove ${filter.value}"
            >
              <span aria-hidden="true">×</span>
            </button>
          </span>
        `
          )
          .join('')}

        <button type="button" class="clear-all-filters" data-action="clear-all">Clear All</button>
      </div>
    `;

    // Add event listeners
    this.addEventListener('click', (e) => {
      const removeButton = e.target.closest('.remove-filter');
      if (removeButton) {
        const key = removeButton.dataset.key;
        const value = removeButton.dataset.value;
        window.collectionAPI?.removeFilter(key, value);
      } else if (e.target.classList.contains('clear-all-filters')) {
        window.collectionAPI?.clearAllFilters();
      }
    });
  }
}

// Custom element for content block banners
class RTContentBlock extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const bannerData = this.getAttribute('data-banner');
    if (bannerData) {
      this.banner = JSON.parse(bannerData);
      this.render();
    }
  }

  render() {
    if (!this.banner) return;

    const {
      width,
      height,
      media_url,
      display_title,
      link,
      external_video,
      aspect_ratio,
      title,
      subtitle,
      link_text,
      hide_button,
    } = this.banner;

    // Sanitize user inputs to prevent XSS
    const sanitizedTitle = (display_title || '').replace(/[<>]/g, '');
    const sanitizedLink = (link || '#').replace(/javascript:/gi, '').replace(/on\w+=/gi, '');

    // Determine width class based on metafield value
    // Normalize width value (lowercase, remove spaces and special chars)
    const normalizedWidth = (width || 'normal').toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedHeight = (height || 'normal').toLowerCase().replace(/[^a-z0-9]/g, '');
    let widthClass = 'width-normal'; // Default
    let heightClass = 'height-normal'; //Default

    if (normalizedHeight === 'wide') {
      heightClass = 'height-wide'; //Takes 2 product card spcaes
    }

    if (normalizedWidth === 'wide') {
      widthClass = 'width-wide'; // Takes 2 product card spaces
    } else if (
      normalizedWidth === '34' ||
      normalizedWidth === 'threequarter' ||
      normalizedWidth === '3quarter'
    ) {
      widthClass = 'width-three-quarter'; // Takes 3 product card spaces
    } else if (normalizedWidth === 'full' || normalizedWidth === 'fullwidth') {
      widthClass = 'width-full'; // Takes full row width
    } else if (normalizedWidth === 'normal') {
      widthClass = 'width-normal'; // Takes 1 product card space
    }

    // Apply width class to the element itself
    this.className = `${widthClass} ${heightClass}`;

    // Determine background content (external video or image)
    let backgroundContent = '';
    if (external_video && external_video.trim() !== '') {
      // Handle external video (YouTube, Vimeo, etc.)
      const videoId = this.extractVideoId(external_video);
      if (videoId.type === 'youtube') {
        backgroundContent = `
          <iframe
            src="https://www.youtube.com/embed/${videoId.id}?autoplay=1&mute=1&loop=1&playlist=${videoId.id}&controls=0&showinfo=0&rel=0"
            class="content-block-banner__background"
            frameborder="0"
            allow="autoplay; encrypted-media"
            loading="lazy"
            style="aspect-ratio: ${aspect_ratio || '16/9'};"
          ></iframe>
        `;
      } else if (videoId.type === 'vimeo') {
        backgroundContent = `
          <iframe
            src="https://player.vimeo.com/video/${videoId.id}?autoplay=1&muted=1&loop=1&background=1"
            class="content-block-banner__background"
            frameborder="0"
            allow="autoplay; fullscreen"
            loading="lazy"
            style="aspect-ratio: ${aspect_ratio || '16/9'};"
          ></iframe>
        `;
      }
    } else if (media_url) {
      backgroundContent = `
        <img
          src="${media_url}"
          alt="${sanitizedTitle || 'Banner'}"
          class="content-block-banner__background"
          loading="lazy"
        />
      `;
    }

    let contentInfo = '';
    if (title || subtitle) {
      if (title) {
        contentInfo += `<span class="content-block-title">${title}</span>`;
      }
      if (subtitle) {
        contentInfo += `<span class="content-block-subtitle">${subtitle}</span>`;
      }
      if (link && link_text && !hide_button) {
        contentInfo += `<a class="content-block-link" href="${link}">${link_text}</a>`;
      }
    }

    this.innerHTML = `
      <div class="content-block-wrapper ${contentInfo != '' ? 'has-info' : ''}">
        <a href="${sanitizedLink}" class="content-block-banner">
          ${backgroundContent}
          <div class="content-block-banner__overlay"></div>
          <div class="content-block-banner__content">
            <h2 class="content-block-banner__title">${sanitizedTitle}</h2>
            ${link && !hide_button && contentInfo == '' ? '<span class="content-block-banner__button">' + (link_text ? link_text : 'Shop Now') + '</span>' : ''}
          </div>
        </a>
        ${contentInfo != '' ? '<div class="content-block-info">' + contentInfo + '</div>' : ''}
      </div>
    `;
  }

  extractVideoId(url) {
    // Extract YouTube video ID
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return { type: 'youtube', id: youtubeMatch[1] };
    }

    // Extract Vimeo video ID
    const vimeoRegex = /(?:vimeo\.com\/)(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return { type: 'vimeo', id: vimeoMatch[1] };
    }

    return { type: 'unknown', id: null };
  }
}

// Custom element for filters drawer (mobile)
class RTFiltersDrawer extends HTMLElement {
  connectedCallback() {
    this.className = 'filters-drawer';
    this.pendingFilters = {}; // Store pending filter changes

    this.innerHTML = `
      <div class="filters-drawer__overlay"></div>
      <div class="filters-drawer__content">
        <div class="filters-drawer__header">
          <p class="h4">Filters</p>
          <button class="filters-drawer__close">
            <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
              <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
        </div>
        <div class="filters-drawer__body">
          <rt-filters></rt-filters>
        </div>
        <div class="filters-drawer__footer">
          <button class="apply-filters button w-full">View results</button>
        </div>
      </div>
    `;

    // Add event listeners
    this.querySelector('.filters-drawer__close').addEventListener('click', () => this.close());
    this.querySelector('.filters-drawer__overlay').addEventListener('click', () => this.close());
    this.querySelector('.apply-filters').addEventListener('click', () => {
      this.applyPendingFilters();
      this.close();
    });

    // Listen for filter changes within the drawer
    this.addEventListener('filterChange', (e) => {
      // Store the filter change but don't apply it yet
      Object.assign(this.pendingFilters, e.detail);
    });
  }

  open() {
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Ensure filters are loaded when drawer opens
    if (window.collectionAPI && window.collectionAPI.isExternalApiEnabled()) {
      // Trigger a refresh of filters in the drawer
      const filtersElement = this.querySelector('rt-filters');
      if (filtersElement && window.collectionAPI.currentFilters) {
        // Re-render filters to ensure they're up to date
        setTimeout(() => {
          filtersElement.updateAllFilterStates();
        }, 100);
      }
    }
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  setFilters(filters) {
    const filtersElement = this.querySelector('rt-filters');
    if (filtersElement) {
      filtersElement.setFilters(filters);
    }
  }

  applyPendingFilters() {
    // Apply all pending filter changes
    if (Object.keys(this.pendingFilters).length > 0) {
      // Apply all pending filter changes at once
      document.dispatchEvent(
        new CustomEvent('filterChange', {
          detail: this.pendingFilters,
          bubbles: true,
        })
      );

      // Also ensure the CollectionAPI processes the filter changes
      if (window.collectionAPI && window.collectionAPI.isExternalApiEnabled()) {
        setTimeout(() => {
          if (window.collectionAPI) {
            window.collectionAPI.loadData();
          }
        }, 100);
      }

      // Clear pending filters after applying
      this.pendingFilters = {};
    }
  }
}

// Register custom elements
customElements.define('rt-product-list', RTProductList);
customElements.define('rt-product-card', RTProductCard);
customElements.define('rt-filters', RTFilters);
customElements.define('rt-filter', RTFilter);
customElements.define('rt-paginate', RTPaginate);
customElements.define('rt-sort-by', RTSortBy);
customElements.define('rt-active-filters', RTActiveFilters);
customElements.define('rt-filters-drawer', RTFiltersDrawer);
customElements.define('rt-content-block', RTContentBlock);

// Initialize the collection API when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.collectionAPI = new CollectionAPI();

  // If external API is not enabled, let the standard Shopify facets handle everything
  if (!window.collectionAPI.isExternalApiEnabled()) {
    console.log('External API disabled - using standard Shopify facets');
    return;
  }

  console.log('External API enabled - initializing CollectionAPI');
  window.collectionAPI.init();

  // Intercept facet:update events and prevent standard facets when external API is enabled
  document.addEventListener(
    'facet:update',
    (event) => {
      if (window.collectionAPI && window.collectionAPI.isExternalApiEnabled()) {
        event.stopImmediatePropagation();
        event.preventDefault();

        // Extract sort value and handle it with our external API
        const url = new URL(event.detail.url);
        const sortValue = url.searchParams.get('sort_by');
        if (sortValue) {
          window.collectionAPI.updateSort(sortValue);
        }
      }
    },
    true
  ); // Use capture phase to catch the event before other listeners
});
